// ConfiguraciÃ³n de Supabase para mÃºltiples entornos
import { supabase } from '../lib/supabase';
import {
  isEdicionNombreLinkValido,
  normalizeEdicionNombreLink,
} from '../lib/utils/edicionNombreLink';
import { syncSedesRestauranteAPropuestas } from '../lib/utils/syncSedesRestaurantePropuestas';
import {
  parseCatalogoBannerColores,
  type CatalogoBannerColores,
} from '../lib/utils/catalogoBannerColores';

console.log('ðŸ”§ Entorno detectado:', (typeof __DEV__ !== 'undefined' ? __DEV__ : process.env.NODE_ENV !== 'production') ? 'DESARROLLO' : 'PRODUCCIÃ“N');

// Exportar supabase para uso en otros archivos
export { supabase };

/** Migración pendiente: supabase/migrations/20260404120000_restaurantes_sede_principal_coords.sql */
function isMissingSedePrincipalCoordsColumnsError(error: unknown): boolean {
  const e = error as { code?: string; message?: string };
  if (e?.code !== 'PGRST204' || !e?.message) return false;
  return (
    e.message.includes('latitud_sede_principal') ||
    e.message.includes('longitud_sede_principal')
  );
}

function omitSedePrincipalCoords<T extends Record<string, unknown>>(row: T): T {
  const { latitud_sede_principal: _la, longitud_sede_principal: _lo, ...rest } = row;
  return rest as T;
}

export interface Ciudad {
  id: string;
  nombre: string;
  cover_web?: string;
  cover_mobile?: string;
  fechaCreacion: string;
  admin_id?: string;
  admin_nombre?: string;
  patrocinadores_ids?: string[];
  patrocinadores_nombres?: string[];
  /** Referencia para comensales: ciudad más cercana por GPS */
  latitud_centro?: number | null;
  longitud_centro?: number | null;
}

export interface Patrocinador {
  id: string;
  nombre: string;
  email?: string;
  telefono?: string;
  representante?: string;
  logo_fondo_claro?: string;
  logo_fondo_oscuro?: string;
  fechaCreacion: string;
  ciudades_nombres: string[];
  ciudades_ids: string[];
}

export interface Sede {
  nombre: string;
  ubicacion_corta: string;
  horarios: string;
  ubicacion_gps?: string;
  numero_mesas?: number;
  direccion_completa?: string;
  /** Opcional; sedes adicionales en JSON `restaurantes.sedes` */
  latitud?: number | null;
  longitud?: number | null;
}

export interface Restaurante {
  id: string;
  nombre_oficial: string;
  rif?: string;
  nombre_mostrar: string;
  /** Ventas promedio (entero); opcional. */
  ventas_promedio?: number | null;
  /** Si es true, /cdash exige imagen de comprobante al guardar un reporte de ventas. */
  comprobante_reporte_ventas_obligatorio?: boolean;
  breve_resena?: string;
  slogan?: string;
  notas?: string;
  representante?: string;
  ciudad_id?: string;
  ciudad_nombre?: string;
  email?: string;
  telefono?: string;
  instagram?: string;
  logo?: string;
  logos?: Array<{ url: string; id: string }>;
  logo_seleccionado_index?: number;
  fachadas?: Array<{ url: string; id: string }>;
  sede_ubicacion_corta?: string;
  direccion_completa?: string;
  ubicacion_gps?: string;
  sede_numero_mesas?: number;
  sede_horario?: string;
  sedes?: Sede[];
  /** Coordenadas opcionales de la sede principal (columnas en BD) */
  latitud_sede_principal?: number | null;
  longitud_sede_principal?: number | null;
  propuestas?: string;
  ediciones?: string;
  premios_obtenidos?: string;
  fecha_creacion: string;
  // Relaciones
  ediciones_ids?: string[];
  ediciones_nombres?: string[];
  /** Alineado con `ediciones_ids` (YYYY-MM-DD o ISO). */
  ediciones_fecha_inicio?: string[];
  /** Alineado con `ediciones_ids` (YYYY-MM-DD o ISO). */
  ediciones_fecha_fin?: string[];
  /** Alineado con `ediciones_ids`: si el comprobante de imagen es obligatorio en /cdash para esa edición. */
  ediciones_comprobante_reporte_ventas_obligatorio?: boolean[];
  /** Alineado con `ediciones_ids`: precio de la propuesta de cada edición (null si no está definido). */
  ediciones_precio_propuesta?: (number | null)[];
  /** UUID único para QR público (`/r/:codigo_qr`) */
  codigo_qr?: string;
  /** Código alfanumérico de 8 caracteres para acceso a /cdash */
  codigo_cdash?: string;
  /** Si true, en votación se permite bypass de GPS (Recargar inferior). */
  bypass_ubicacion?: boolean;
  /** Si true, en votación se puede omitir verificación de distancia al local. */
  bypass_rango_restaurante?: boolean;
  /** Flag de dashboard (icono votar); uso posterior. */
  bypass_votacion?: boolean;
}

export interface ItemVotacion {
  titulo: string;
  subtitulo: string;
  /**
   * Restaurantes donde el ítem es visible al votar.
   * Vacío o ausente = visible en todos los restaurantes de la edición.
   */
  restaurantes_ids?: string[];
  /**
   * Si false, el ítem no entra en el cálculo de ganadores (Votos → Top 3).
   * Ausente o true = sí cuenta. Por defecto todos cuentan.
   */
  cuenta_en_ganadores?: boolean;
}

export function normalizeRestaurantesIdsItemVotacion(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  const out: string[] = [];
  const seen = new Set<string>();
  for (const x of raw) {
    const id = String(x ?? '').trim();
    if (!id || seen.has(id)) continue;
    seen.add(id);
    out.push(id);
  }
  return out;
}

export function parseItemsVotacion(raw: unknown): ItemVotacion[] {
  let arr: unknown[] = [];
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      arr = Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  } else if (Array.isArray(raw)) {
    arr = raw;
  } else {
    return [];
  }
  return arr.map((it) => {
    const o = it && typeof it === 'object' ? (it as Record<string, unknown>) : {};
    return {
      titulo: String(o.titulo ?? ''),
      subtitulo: String(o.subtitulo ?? ''),
      restaurantes_ids: normalizeRestaurantesIdsItemVotacion(o.restaurantes_ids),
      cuenta_en_ganadores: o.cuenta_en_ganadores === false ? false : true,
    };
  });
}

export function itemVotacionCuentaEnGanadores(
  item: ItemVotacion | null | undefined
): boolean {
  return item?.cuenta_en_ganadores !== false;
}

/**
 * Índices de ítems que cuentan para Top 3 / ranking.
 * `null` = todos (configuración por defecto).
 */
export function indicesItemsCuentaEnGanadores(
  items: ItemVotacion[] | null | undefined
): number[] | null {
  const list = items ?? [];
  if (list.length === 0) return null;
  const included: number[] = [];
  for (let i = 0; i < list.length; i += 1) {
    if (itemVotacionCuentaEnGanadores(list[i])) included.push(i);
  }
  if (included.length === list.length) return null;
  return included;
}

export function serializeItemVotacion(item: ItemVotacion): ItemVotacion {
  const restaurantes_ids = normalizeRestaurantesIdsItemVotacion(item.restaurantes_ids);
  const out: ItemVotacion = {
    titulo: String(item.titulo ?? '').trim(),
    subtitulo: String(item.subtitulo ?? '').trim(),
    restaurantes_ids,
  };
  if (item.cuenta_en_ganadores === false) {
    out.cuenta_en_ganadores = false;
  }
  return out;
}

export function itemVotacionVisibleParaRestaurante(
  item: ItemVotacion,
  restauranteId: string | number | null | undefined
): boolean {
  const ids = item.restaurantes_ids;
  if (!ids || ids.length === 0) return true;
  if (restauranteId == null || String(restauranteId).trim() === '') return true;
  const rid = String(restauranteId);
  return ids.some((id) => String(id) === rid);
}

export function filtrarItemsVotacionParaRestaurante(
  items: ItemVotacion[] | null | undefined,
  restauranteId: string | number | null | undefined
): Array<ItemVotacion & { originalIndex: number }> {
  return (items ?? [])
    .map((item, originalIndex) => ({ ...item, originalIndex }))
    .filter((item) => itemVotacionVisibleParaRestaurante(item, restauranteId));
}

export type EdicionEstado = 'activa' | 'planificada' | 'anunciada' | 'catalogo' | 'finalizada';

/** Publicación de propuestas por ciudad dentro de una edición (`ediciones_ciudades`). */
export interface EdicionCiudadPublicacion {
  ciudad_id: string;
  fecha_publicacion: string | null;
  habilitado: boolean;
}

export interface Edicion {
  id: number;
  nombre: string;
  /** Segmento de URL pública: /edicion/[nombre_link] */
  nombre_link?: string | null;
  categoria?: string;
  /** Títulos/trofeos de la edición (multiselección). */
  trofeos?: string[];
  descripcion?: string;
  fecha_inicio?: string;
  fecha_fin?: string;
  /** Opcional: día en que se anuncia / se hace pública la edición */
  fecha_anuncio?: string | null;
  /** Opcional: día de apertura del catálogo cuando el estado es "catalogo" */
  fecha_catalogo?: string | null;
  /** Colores del banner de fechas en el catálogo (inicia / vigente / finalizado). */
  catalogo_banner_colores?: CatalogoBannerColores;
  logotema?: string;
  portada_web?: string;
  portada_mobile?: string;
  portada2_web?: string;
  portada2_mobile?: string;
  /** Portada promocional para la app (banner comensal, 16:9). */
  portada_app?: string;
  imagen_principal?: string;
  estado: EdicionEstado;
  /** Si true, el estado se calcula según fechas al abrir el formulario (persistido). */
  estado_automatico?: boolean;
  /**
   * Si true, permite votar aunque `estado` no sea `activa` (pruebas internas).
   * Independiente del estatus público de la edición.
   */
  modo_prueba?: boolean;
  /**
   * Si true (por defecto), en /cdash la imagen de comprobante es obligatoria al crear un reporte de ventas
   * para restaurantes vinculados a esta edición.
   */
  comprobante_reporte_ventas_obligatorio?: boolean;
  /** Precio de la propuesta de la edición (opcional). */
  precio_propuesta?: number | null;
  /**
   * Pesos por defecto de la fórmula clásica (dashboard Votos → Top 3); deben sumar 100.
   * Si faltan en datos antiguos, la app usa 20 / 20 / 40 / 20.
   */
  top3_clasica_peso_ventas?: number;
  top3_clasica_peso_pct_votos_hechos?: number;
  top3_clasica_peso_promedio?: number;
  top3_clasica_peso_pct_perfectos?: number;
  /** Paleta de colores de la pantalla de votación (hex #RRGGBB). */
  votacion_color_titulo?: string;
  votacion_color_subtitulo?: string;
  votacion_color_fondo?: string;
  votacion_color_boton?: string;
  votacion_color_texto_boton?: string;
  /** Paleta pantalla voto exitoso. */
  votacion_exitoso_color_titulo?: string;
  votacion_exitoso_color_subtitulo?: string;
  votacion_exitoso_color_fondo?: string;
  votacion_exitoso_color_boton?: string;
  votacion_exitoso_color_texto_boton?: string;
  /** CTA en pantalla de voto exitoso. */
  votacion_exitoso_cta_texto?: string;
  votacion_exitoso_cta_texto_descriptivo?: string;
  votacion_exitoso_cta_link?: string;
  votacion_exitoso_cta_icono?: string;
  /** Paleta pantalla voto fallido. */
  votacion_fallido_color_titulo?: string;
  votacion_fallido_color_subtitulo?: string;
  votacion_fallido_color_fondo?: string;
  votacion_fallido_color_boton?: string;
  votacion_fallido_color_texto_boton?: string;
  /** Distancia máxima en metros para poder votar (geocerca). */
  votacion_distancia_minima_metros?: number;
  fecha_creacion: string;
  items_votacion?: ItemVotacion[];
  // Relaciones
  ciudades_ids?: string[];
  ciudades_nombres?: string[];
  /** Alineado con `ciudades_ids`: portada de cada ciudad en la edición (opcional). */
  ciudades_cover_web?: (string | null | undefined)[];
  ciudades_cover_mobile?: (string | null | undefined)[];
  /** Fecha + toggle de publicación de propuestas por ciudad (alineado con `ciudades_ids`). */
  ciudades_publicacion?: EdicionCiudadPublicacion[];
  patrocinadores_ids?: string[];
  patrocinadores_nombres?: string[];
  restaurantes_ids?: string[];
  restaurantes_nombres?: string[];
}

export interface Voto {
  id: string;
  restaurante_id: string;
  restaurante_nombre?: string;
  edicion_id: string;
  edicion_nombre?: string;
  usuario_id?: string;
  usuario_nombre?: string;
  puntuacion: number;
  comentario?: string;
  fecha_creacion: string;
}

export interface Propuesta {
  id: string;
  nombre: string;
  descripcion?: string;
  fecha_creacion?: string;
}

export interface Estadistica {
  id: string;
  restaurante_id: string;
  restaurante_nombre?: string;
  edicion_id: string;
  edicion_nombre?: string;
  total_votos: number;
  promedio_puntuacion: number;
  fecha_actualizacion: string;
}

// Tipos para eventos SSE
export interface SSEEvent {
  type: string;
  data: any;
  timestamp: string;
}

// Callbacks para actualizaciones en tiempo real
let onCiudadAgregada: ((ciudad: Ciudad) => void) | null = null;
let onCiudadActualizada: ((ciudad: Ciudad) => void) | null = null;
let onCiudadEliminada: ((id: string) => void) | null = null;

// FunciÃ³n para configurar callbacks de eventos
export const configurarEventosTiempoReal = (callbacks: {
  onCiudadAgregada?: (ciudad: Ciudad) => void;
  onCiudadActualizada?: (ciudad: Ciudad) => void;
  onCiudadEliminada?: (id: string) => void;
}) => {
  onCiudadAgregada = callbacks.onCiudadAgregada || null;
  onCiudadActualizada = callbacks.onCiudadActualizada || null;
  onCiudadEliminada = callbacks.onCiudadEliminada || null;
};

// FunciÃ³n para iniciar polling en tiempo real
export const iniciarConexionTiempoReal = () => {
  let lastUpdate = new Date().toISOString();
  let isPolling = true;
  
  const pollForUpdates = async () => {
    if (!isPolling) return;
    
    try {
      // Obtener todas las ciudades y comparar con la Ãºltima actualizaciÃ³n
      const ciudades = await getCiudades();
      
      // Si hay ciudades mÃ¡s recientes que la Ãºltima actualizaciÃ³n
      const nuevasCiudades = ciudades.filter(ciudad => 
        new Date(ciudad.fechaCreacion) > new Date(lastUpdate)
      );
      
      if (nuevasCiudades.length > 0) {
        console.log('ðŸ†• Nuevas ciudades detectadas:', nuevasCiudades.length);
        // Actualizar la Ãºltima fecha
        lastUpdate = new Date().toISOString();
        
        // Notificar sobre las nuevas ciudades
        nuevasCiudades.forEach(ciudad => {
          if (onCiudadAgregada) {
            onCiudadAgregada(ciudad);
          }
        });
      }
    } catch (error) {
      console.error('Error en polling:', error);
    }
    
    // Continuar polling cada 3 segundos
    if (isPolling) {
      setTimeout(pollForUpdates, 3000);
    }
  };
  
  // Iniciar polling
  pollForUpdates();
  
  // Retornar funciÃ³n para detener polling
  return {
    close: () => {
      isPolling = false;
    }
  };
};

// FunciÃ³n helper para manejar errores de Supabase
const handleSupabaseError = (error: any) => {
  console.error('Supabase error:', error);
  throw new Error(error.message || 'Error en la operaciÃ³n');
};

// Obtener todas las ciudades
export const getCiudades = async (): Promise<Ciudad[]> => {
  try {
    const { data: ciudadesData, error: ciudadesError } = await supabase
      .from('ciudades')
      .select(`
        *,
        patrocinadores_ciudades(
          patrocinadores(id, nombre)
        )
      `)
      .order('fecha_creacion', { ascending: false });

    if (ciudadesError) throw ciudadesError;
    if (!ciudadesData) return [];

    // Obtener admin_ids Ãºnicos
    const adminIds = ciudadesData
      .map(c => c.admin_id)
      .filter((id): id is string => !!id);
    
    // Obtener perfiles de admins si hay alguno
    let adminProfilesMap = new Map<string, { nombre?: string, apellido?: string }>();
    if (adminIds.length > 0) {
      const { data: adminsData, error: adminsError } = await supabase
        .from('user_profiles')
        .select('id, nombre, apellido')
        .in('id', adminIds);
      
      if (!adminsError && adminsData) {
        adminsData.forEach((admin: any) => {
          adminProfilesMap.set(admin.id, {
            nombre: admin.nombre,
            apellido: admin.apellido,
          });
        });
      }
    }

    return ciudadesData.map((ciudad: any) => {
      const adminProfile = ciudad.admin_id ? adminProfilesMap.get(ciudad.admin_id) : null;
      const adminNombre = adminProfile 
        ? [adminProfile.nombre, adminProfile.apellido].filter(Boolean).join(' ') || undefined
        : undefined;
      
      // Obtener patrocinadores de la ciudad
      const patrocinadores = ciudad.patrocinadores_ciudades || [];
      const patrocinadoresIds = patrocinadores
        .map((pc: any) => pc.patrocinadores?.id?.toString())
        .filter((id: any): id is string => !!id);
      const patrocinadoresNombres = patrocinadores
        .map((pc: any) => pc.patrocinadores?.nombre)
        .filter((nombre: any): nombre is string => !!nombre);
      
      return {
        id: ciudad.id.toString(),
        nombre: ciudad.nombre,
        cover_web: ciudad.cover_web,
        cover_mobile: ciudad.cover_mobile,
        fechaCreacion: ciudad.fecha_creacion,
        admin_id: ciudad.admin_id,
        admin_nombre: adminNombre,
        patrocinadores_ids: patrocinadoresIds.length > 0 ? patrocinadoresIds : undefined,
        patrocinadores_nombres: patrocinadoresNombres.length > 0 ? patrocinadoresNombres : undefined,
        latitud_centro: ciudad.latitud_centro ?? null,
        longitud_centro: ciudad.longitud_centro ?? null,
      };
    });
  } catch (error) {
    console.error('Error obteniendo ciudades:', error);
    return [];
  }
};

// Agregar nueva ciudad
export const addCiudad = async (
  nombre: string,
  cover_web?: string,
  cover_mobile?: string,
  admin_id?: string,
  latitud_centro?: number | null,
  longitud_centro?: number | null
): Promise<Ciudad> => {
  try {
    const ciudadData: any = { nombre, cover_web, cover_mobile };
    if (admin_id) {
      ciudadData.admin_id = admin_id;
    }
    if (latitud_centro !== undefined && latitud_centro !== null) {
      ciudadData.latitud_centro = latitud_centro;
    }
    if (longitud_centro !== undefined && longitud_centro !== null) {
      ciudadData.longitud_centro = longitud_centro;
    }

    const { data, error } = await supabase
      .from('ciudades')
      .insert([ciudadData])
      .select()
      .single();

    if (error) throw error;
    
    // Si hay admin_id, asegurar que existe la relación en usuarios_ciudades
    if (admin_id) {
      try {
        await supabase
          .from('usuarios_ciudades')
          .upsert({
            usuario_id: admin_id,
            ciudad_id: parseInt(data.id),
          }, {
            onConflict: 'usuario_id,ciudad_id'
          });
      } catch (relError) {
        console.warn('Error al crear relación usuario-ciudad al agregar ciudad:', relError);
        // No fallar la creación de la ciudad si esto falla
      }
    }
    
    return {
      id: data.id.toString(),
      nombre: data.nombre,
      cover_web: data.cover_web,
      cover_mobile: data.cover_mobile,
      fechaCreacion: data.fecha_creacion,
      admin_id: data.admin_id,
      latitud_centro: data.latitud_centro ?? null,
      longitud_centro: data.longitud_centro ?? null,
    };
  } catch (error) {
    console.error('Error agregando ciudad:', error);
    throw error;
  }
};

// Eliminar ciudad
export const deleteCiudad = async (id: string): Promise<void> => {
  try {
    const { error } = await supabase
      .from('ciudades')
      .delete()
      .eq('id', id);

    if (error) throw error;
  } catch (error) {
    console.error('Error eliminando ciudad:', error);
    throw error;
  }
};

// Buscar ciudades
export const searchCiudades = async (query: string): Promise<Ciudad[]> => {
  try {
    const { data: ciudadesData, error: ciudadesError } = await supabase
      .from('ciudades')
      .select(`
        *,
        patrocinadores_ciudades(
          patrocinadores(id, nombre)
        )
      `)
      .ilike('nombre', `%${query}%`)
      .order('fecha_creacion', { ascending: false });

    if (ciudadesError) throw ciudadesError;
    if (!ciudadesData) return [];

    // Obtener admin_ids Ãºnicos
    const adminIds = ciudadesData
      .map(c => c.admin_id)
      .filter((id): id is string => !!id);
    
    // Obtener perfiles de admins si hay alguno
    let adminProfilesMap = new Map<string, { nombre?: string, apellido?: string }>();
    if (adminIds.length > 0) {
      const { data: adminsData, error: adminsError } = await supabase
        .from('user_profiles')
        .select('id, nombre, apellido')
        .in('id', adminIds);
      
      if (!adminsError && adminsData) {
        adminsData.forEach((admin: any) => {
          adminProfilesMap.set(admin.id, {
            nombre: admin.nombre,
            apellido: admin.apellido,
          });
        });
      }
    }

    return ciudadesData.map((ciudad: any) => {
      const adminProfile = ciudad.admin_id ? adminProfilesMap.get(ciudad.admin_id) : null;
      const adminNombre = adminProfile 
        ? [adminProfile.nombre, adminProfile.apellido].filter(Boolean).join(' ') || undefined
        : undefined;
      
      // Obtener patrocinadores de la ciudad
      const patrocinadores = ciudad.patrocinadores_ciudades || [];
      const patrocinadoresIds = patrocinadores
        .map((pc: any) => pc.patrocinadores?.id?.toString())
        .filter((id: any): id is string => !!id);
      const patrocinadoresNombres = patrocinadores
        .map((pc: any) => pc.patrocinadores?.nombre)
        .filter((nombre: any): nombre is string => !!nombre);
      
      return {
        id: ciudad.id.toString(),
        nombre: ciudad.nombre,
        cover_web: ciudad.cover_web,
        cover_mobile: ciudad.cover_mobile,
        fechaCreacion: ciudad.fecha_creacion,
        admin_id: ciudad.admin_id,
        admin_nombre: adminNombre,
        patrocinadores_ids: patrocinadoresIds.length > 0 ? patrocinadoresIds : undefined,
        patrocinadores_nombres: patrocinadoresNombres.length > 0 ? patrocinadoresNombres : undefined,
        latitud_centro: ciudad.latitud_centro ?? null,
        longitud_centro: ciudad.longitud_centro ?? null,
      };
    });
  } catch (error) {
    console.error('Error buscando ciudades:', error);
    return [];
  }
};

// Actualizar ciudad
export const updateCiudad = async (
  id: string,
  nombre: string,
  cover_web?: string,
  cover_mobile?: string,
  admin_id?: string,
  latitud_centro?: number | null,
  longitud_centro?: number | null
): Promise<Ciudad> => {
  try {
    // Primero obtener la ciudad actual para comparar admin_id
    const { data: ciudadActual, error: fetchError } = await supabase
      .from('ciudades')
      .select('admin_id')
      .eq('id', id)
      .single();

    if (fetchError && fetchError.code !== 'PGRST116') {
      // PGRST116 = not found, pero continuamos
      console.warn('Error al obtener ciudad actual:', fetchError);
    }

    const adminIdAnterior = ciudadActual?.admin_id;
    
    // Construir los datos de actualización
    const updateData: any = { nombre, cover_web, cover_mobile };

    if (latitud_centro !== undefined) {
      updateData.latitud_centro = latitud_centro;
    }
    if (longitud_centro !== undefined) {
      updateData.longitud_centro = longitud_centro;
    }
    
    // Actualizar admin_id solo si se proporciona explícitamente
    if (admin_id !== undefined) {
      updateData.admin_id = admin_id === '' ? null : admin_id;
    }
    
    // Determinar el nuevo admin_id para comparar y actualizar relaciones
    const adminIdNuevo = admin_id === undefined ? adminIdAnterior : (admin_id === '' ? null : admin_id);

    const { data, error } = await supabase
      .from('ciudades')
      .update(updateData)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    
    // Si cambió el admin_id, actualizar relaciones en usuarios_ciudades
    if (adminIdAnterior !== adminIdNuevo) {
      try {
        // Si había un admin anterior y ahora no hay admin, eliminar la relación
        if (adminIdAnterior && !adminIdNuevo) {
          await supabase
            .from('usuarios_ciudades')
            .delete()
            .eq('usuario_id', adminIdAnterior)
            .eq('ciudad_id', parseInt(id));
        }
        
        // Si hay un nuevo admin, asegurar que existe la relación
        if (adminIdNuevo) {
          await supabase
            .from('usuarios_ciudades')
            .upsert({
              usuario_id: adminIdNuevo,
              ciudad_id: parseInt(id),
            }, {
              onConflict: 'usuario_id,ciudad_id'
            });
          
          // Si había un admin anterior diferente, eliminar la relación anterior
          if (adminIdAnterior && adminIdAnterior !== adminIdNuevo) {
            await supabase
              .from('usuarios_ciudades')
              .delete()
              .eq('usuario_id', adminIdAnterior)
              .eq('ciudad_id', parseInt(id));
          }
        }
      } catch (relError) {
        console.warn('Error al actualizar relación usuario-ciudad:', relError);
        // No fallar la actualización de la ciudad si esto falla
      }
    }
    
    return {
      id: data.id.toString(),
      nombre: data.nombre,
      cover_web: data.cover_web,
      cover_mobile: data.cover_mobile,
      fechaCreacion: data.fecha_creacion,
      admin_id: data.admin_id,
      latitud_centro: data.latitud_centro ?? null,
      longitud_centro: data.longitud_centro ?? null,
    };
  } catch (error) {
    const e = error as { message?: string; code?: string; details?: string; hint?: string };
    console.error('Error actualizando ciudad:', e?.message ?? String(error), {
      code: e?.code,
      details: e?.details,
      hint: e?.hint,
    });
    throw error;
  }
};

// Funciones para patrocinadores

// Obtener todos los patrocinadores
export const getPatrocinadores = async (): Promise<Patrocinador[]> => {
  try {
    const { data, error } = await supabase
      .from('patrocinadores')
      .select(`
        *,
        patrocinadores_ciudades(
          ciudades(id, nombre)
        )
      `)
      .order('fecha_creacion', { ascending: false });

    if (error) throw error;

    return data.map((patrocinador: any) => ({
      id: patrocinador.id.toString(),
      nombre: patrocinador.nombre || '',
      email: patrocinador.email || '',
      telefono: patrocinador.telefono || '',
      representante: patrocinador.representante || '',
      logo_fondo_claro: patrocinador.logo_fondo_claro || '',
      logo_fondo_oscuro: patrocinador.logo_fondo_oscuro || '',
      fechaCreacion: patrocinador.fecha_creacion || new Date().toISOString(),
      ciudades_nombres: patrocinador.patrocinadores_ciudades?.map((pc: any) => pc.ciudades?.nombre).filter(Boolean) || [],
      ciudades_ids: patrocinador.patrocinadores_ciudades?.map((pc: any) => pc.ciudades?.id?.toString()).filter(Boolean) || [],
    }));
  } catch (error) {
    console.error('Error obteniendo patrocinadores:', error);
    return [];
  }
};

// Agregar nuevo patrocinador
export const addPatrocinador = async (patrocinadorData: {
  nombre: string;
  email?: string | null;
  telefono?: string;
  representante?: string;
  logo_fondo_claro?: string;
  logo_fondo_oscuro?: string;
  ciudades_ids?: string[];
}): Promise<Patrocinador> => {
  console.log('ðŸ” addPatrocinador llamado con:', patrocinadorData);
  
  try {
    // Extraer ciudades_ids del patrocinadorData
    const { ciudades_ids, ...rest } = patrocinadorData;
    const emailTrim = (rest.email ?? '').trim();
    const patrocinadorInfo = {
      ...rest,
      email: emailTrim === '' ? null : emailTrim,
    };

    // Insertar el patrocinador
    const { data: nuevoPatrocinador, error } = await supabase
      .from('patrocinadores')
      .insert([patrocinadorInfo])
      .select()
      .single();

    if (error) throw error;
    
    console.log('âœ… Patrocinador creado:', nuevoPatrocinador);
    
    // Si hay ciudades, crear las relaciones
    if (ciudades_ids && ciudades_ids.length > 0) {
      const relaciones = ciudades_ids.map(ciudad_id => ({
        patrocinador_id: nuevoPatrocinador.id,
        ciudad_id: parseInt(ciudad_id)
      }));
      
      const { error: errorRelaciones } = await supabase
        .from('patrocinadores_ciudades')
        .insert(relaciones);
        
      if (errorRelaciones) {
        console.error('Error creando relaciones con ciudades:', errorRelaciones);
      }
    }
    
    return {
      id: nuevoPatrocinador.id.toString(),
      nombre: nuevoPatrocinador.nombre,
      email: nuevoPatrocinador.email,
      telefono: nuevoPatrocinador.telefono,
      representante: nuevoPatrocinador.representante,
      logo_fondo_claro: nuevoPatrocinador.logo_fondo_claro,
      logo_fondo_oscuro: nuevoPatrocinador.logo_fondo_oscuro,
      fechaCreacion: nuevoPatrocinador.fecha_creacion,
      ciudades_nombres: [],
      ciudades_ids: ciudades_ids || [],
    };
  } catch (error) {
    console.error('Error agregando patrocinador:', error);
    throw error;
  }
};

// Actualizar patrocinador
export const updatePatrocinador = async (id: string, patrocinadorData: {
  nombre: string;
  email?: string | null;
  telefono?: string;
  representante?: string;
  logo_fondo_claro?: string;
  logo_fondo_oscuro?: string;
  ciudades_ids?: string[];
}): Promise<Patrocinador> => {
  try {
    // Extraer ciudades_ids del patrocinadorData
    const { ciudades_ids, ...rest } = patrocinadorData;
    const emailTrim = (rest.email ?? '').trim();
    const patrocinadorInfo = {
      ...rest,
      email: emailTrim === '' ? null : emailTrim,
    };

    // Actualizar el patrocinador
    const { data, error } = await supabase
      .from('patrocinadores')
      .update(patrocinadorInfo)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    
    // Siempre eliminar relaciones existentes primero
    await supabase
      .from('patrocinadores_ciudades')
      .delete()
      .eq('patrocinador_id', id);
    
    // Si hay ciudades, crear las nuevas relaciones
    if (ciudades_ids && ciudades_ids.length > 0) {
      const relaciones = ciudades_ids.map(ciudad_id => ({
        patrocinador_id: parseInt(id),
        ciudad_id: parseInt(ciudad_id)
      }));
      
      const { error: errorRelaciones } = await supabase
        .from('patrocinadores_ciudades')
        .insert(relaciones);
        
      if (errorRelaciones) {
        console.error('Error actualizando relaciones con ciudades:', errorRelaciones);
      }
    }
    
    return {
      id: data.id.toString(),
      nombre: data.nombre,
      email: data.email,
      telefono: data.telefono,
      representante: data.representante,
      logo_fondo_claro: data.logo_fondo_claro,
      logo_fondo_oscuro: data.logo_fondo_oscuro,
      fechaCreacion: data.fecha_creacion,
      ciudades_nombres: [],
      ciudades_ids: ciudades_ids || [],
    };
  } catch (error) {
    console.error('Error actualizando patrocinador:', error);
    throw error;
  }
};

// Eliminar patrocinador
export const deletePatrocinador = async (id: string): Promise<void> => {
  try {
    const { error } = await supabase
      .from('patrocinadores')
      .delete()
      .eq('id', id);

    if (error) throw error;
  } catch (error) {
    console.error('Error eliminando patrocinador:', error);
    throw error;
  }
};

// Buscar patrocinadores
export const searchPatrocinadores = async (query: string): Promise<Patrocinador[]> => {
  try {
    const { data, error } = await supabase
      .from('patrocinadores')
      .select(`
        *,
        patrocinadores_ciudades!inner(
          ciudades(id, nombre)
        )
      `)
      .or(`nombre.ilike.%${query}%,email.ilike.%${query}%`)
      .order('fecha_creacion', { ascending: false });

    if (error) throw error;

    return data.map((patrocinador: any) => ({
      id: patrocinador.id.toString(),
      nombre: patrocinador.nombre,
      email: patrocinador.email,
      telefono: patrocinador.telefono,
      representante: patrocinador.representante,
      logo_fondo_claro: patrocinador.logo_fondo_claro,
      logo_fondo_oscuro: patrocinador.logo_fondo_oscuro,
      fechaCreacion: patrocinador.fecha_creacion,
      ciudades_nombres: patrocinador.patrocinadores_ciudades?.map((pc: any) => pc.ciudades.nombre) || [],
      ciudades_ids: patrocinador.patrocinadores_ciudades?.map((pc: any) => pc.ciudades.id.toString()) || [],
    }));
  } catch (error) {
    console.error('Error buscando patrocinadores:', error);
    return [];
  }
};

// ========================================
// FUNCIONES PARA RESTAURANTES
// ========================================

// Helper para parsear JSON de forma segura
const parseJSONSafely = (jsonString: string | null | undefined, defaultValue: any = []): any => {
  if (!jsonString || typeof jsonString !== 'string' || jsonString.trim() === '') {
    return defaultValue;
  }
  
  try {
    const parsed = JSON.parse(jsonString);
    return parsed;
  } catch (error) {
    console.warn('Error parseando JSON:', jsonString, error);
    return defaultValue;
  }
};

// Obtener todos los restaurantes
// Si ciudades_ids está presente, filtra restaurantes por esas ciudades (para admins)
/** Mapa restaurante_id -> ediciones relacionadas, en una sola consulta. */
async function getEdicionesPorRestaurante(
  rows: { id?: number | string | null }[]
): Promise<{ [key: string]: { ids: string[]; nombres: string[] } }> {
  const restauranteIds = rows
    .map((r) => r.id)
    .filter((id): id is number | string => id != null && id !== '')
    .map((id) => (typeof id === 'string' ? parseInt(id, 10) : id))
    .filter((id) => !Number.isNaN(id));

  const porRestaurante: { [key: string]: { ids: string[]; nombres: string[] } } = {};
  if (restauranteIds.length === 0) return porRestaurante;

  const { data: relaciones } = await supabase
    .from('restaurantes_ediciones')
    .select(`
      restaurante_id,
      ediciones(id, nombre)
    `)
    .in('restaurante_id', restauranteIds);

  (relaciones ?? []).forEach((relacion: any) => {
    const restauranteId = relacion.restaurante_id.toString();
    if (!porRestaurante[restauranteId]) {
      porRestaurante[restauranteId] = { ids: [], nombres: [] };
    }
    if (relacion.ediciones) {
      porRestaurante[restauranteId].ids.push(relacion.ediciones.id.toString());
      porRestaurante[restauranteId].nombres.push(relacion.ediciones.nombre);
    }
  });

  return porRestaurante;
}

/**
 * Columnas que necesita el listado del dashboard (tabla, tarjetas y filtros).
 * Deja fuera las de texto largo y JSON pesado: `breve_resena`, `logos`,
 * `fachadas`, `premios_obtenidos`, `notas`, `direccion_completa`, etc.
 */
const COLUMNAS_LISTADO_RESTAURANTES = `
  id,
  nombre_oficial,
  nombre_mostrar,
  representante,
  ciudad_id,
  email,
  telefono,
  instagram,
  imagen_principal,
  logos,
  slogan,
  breve_resena,
  direccion,
  direccion_completa,
  ubicacion_gps,
  sedes,
  fecha_creacion,
  codigo_qr,
  bypass_ubicacion,
  bypass_rango_restaurante,
  bypass_votacion,
  ciudades(id, nombre)
`;

export const LISTADO_RESTAURANTES_PAGE_SIZE = 15;

export type ListadoRestaurantesFiltros = {
  ciudades_ids?: string[];
  ciudadId?: string;
  edicionId?: string;
  search?: string;
  offset?: number;
  limit?: number;
};

export type ListadoRestaurantesResultado = {
  rows: Restaurante[];
  total: number;
};

function sanitizarBusquedaListado(raw: string): string {
  return raw
    .trim()
    .replace(/^@+/, '')
    .replace(/[%_,()]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function mapFilaListado(
  restaurante: Record<string, any>,
  edicionesPorRestaurante: { [key: string]: { ids: string[]; nombres: string[] } },
): Restaurante {
  const restauranteId = restaurante.id.toString();
  const ediciones = edicionesPorRestaurante[restauranteId] || { ids: [], nombres: [] };

  return {
    id: restauranteId,
    nombre_oficial: restaurante.nombre_oficial,
    nombre_mostrar: restaurante.nombre_mostrar,
    representante: restaurante.representante,
    ciudad_id: restaurante.ciudad_id?.toString(),
    ciudad_nombre: restaurante.ciudades?.nombre,
    email: restaurante.email,
    telefono: restaurante.telefono,
    instagram: restaurante.instagram || '',
    logo: restaurante.imagen_principal,
    logos: parseJSONSafely(restaurante.logos, []),
    slogan: restaurante.slogan || '',
    breve_resena: restaurante.breve_resena,
    sede_ubicacion_corta: restaurante.direccion,
    direccion_completa: restaurante.direccion_completa || '',
    ubicacion_gps: restaurante.ubicacion_gps || '',
    sedes: parseJSONSafely(restaurante.sedes, []),
    fecha_creacion: restaurante.fecha_creacion,
    ediciones_ids: ediciones.ids,
    ediciones_nombres: ediciones.nombres,
    codigo_qr: restaurante.codigo_qr != null ? String(restaurante.codigo_qr) : undefined,
    bypass_ubicacion: restaurante.bypass_ubicacion === true,
    bypass_rango_restaurante: restaurante.bypass_rango_restaurante === true,
    bypass_votacion: restaurante.bypass_votacion === true,
  };
}

/**
 * Versión ligera y paginada de `getRestaurantes` para el listado del dashboard.
 *
 * Los campos que no pide la consulta llegan vacíos; quien necesite la ficha
 * completa (por ejemplo el modal de edición) debe pedirla con
 * `getRestaurantePorId`.
 */
export const getRestaurantesListado = async (
  filtros: ListadoRestaurantesFiltros = {},
): Promise<ListadoRestaurantesResultado> => {
  try {
    const limit = filtros.limit ?? LISTADO_RESTAURANTES_PAGE_SIZE;
    const offset = filtros.offset ?? 0;
    const select = filtros.edicionId
      ? `${COLUMNAS_LISTADO_RESTAURANTES.trim()},\n  restaurantes_ediciones!inner(edicion_id)`
      : COLUMNAS_LISTADO_RESTAURANTES;

    let query = supabase.from('restaurantes').select(select, { count: 'exact' });

    if (filtros.ciudadId) {
      const ciudadIdInt = parseInt(filtros.ciudadId, 10);
      if (!Number.isNaN(ciudadIdInt)) {
        query = query.eq('ciudad_id', ciudadIdInt);
      }
    } else if (filtros.ciudades_ids && filtros.ciudades_ids.length > 0) {
      const ciudadIdsInt = filtros.ciudades_ids
        .map((id) => parseInt(id, 10))
        .filter((id) => !Number.isNaN(id));
      if (ciudadIdsInt.length > 0) {
        query = query.in('ciudad_id', ciudadIdsInt);
      }
    }

    if (filtros.edicionId) {
      const edicionIdInt = parseInt(filtros.edicionId, 10);
      if (!Number.isNaN(edicionIdInt)) {
        query = query.eq('restaurantes_ediciones.edicion_id', edicionIdInt);
      }
    }

    const search = sanitizarBusquedaListado(filtros.search ?? '');
    if (search) {
      query = query.or(
        [
          `nombre_mostrar.ilike.%${search}%`,
          `nombre_oficial.ilike.%${search}%`,
          `instagram.ilike.%${search}%`,
          `representante.ilike.%${search}%`,
          `email.ilike.%${search}%`,
          `telefono.ilike.%${search}%`,
        ].join(','),
      );
    }

    const { data, error, count } = await query
      .order('nombre_mostrar', { ascending: true })
      .range(offset, offset + limit - 1);
    if (error) throw error;

    const rows = data ?? [];
    if (rows.length === 0) {
      return { rows: [], total: count ?? 0 };
    }

    const edicionesPorRestaurante = await getEdicionesPorRestaurante(
      rows as { id?: number | string | null }[],
    );
    return {
      rows: rows.map((restaurante: Record<string, any>) =>
        mapFilaListado(restaurante, edicionesPorRestaurante),
      ),
      total: count ?? rows.length,
    };
  } catch (error) {
    console.error('Error obteniendo listado de restaurantes:', error);
    throw error;
  }
};

export const getRestaurantes = async (ciudades_ids?: string[]): Promise<Restaurante[]> => {
  try {
    let query = supabase
      .from('restaurantes')
      .select(`
        *,
        ciudades(id, nombre)
      `);

    // Si se proporcionan ciudades_ids, filtrar por ellas (para admins)
    if (ciudades_ids && ciudades_ids.length > 0) {
      const ciudadIdsInt = ciudades_ids.map(id => parseInt(id)).filter(id => !isNaN(id));
      if (ciudadIdsInt.length > 0) {
        query = query.in('ciudad_id', ciudadIdsInt);
      }
    }

    const { data, error } = await query.order('fecha_creacion', { ascending: false });

    if (error) throw error;

    const rows = data ?? [];
    if (rows.length === 0) return [];

    const edicionesPorRestaurante = await getEdicionesPorRestaurante(rows);

    return rows.map((restaurante: any) => {
      const restauranteId = restaurante.id.toString();
      const edicionesRelacionadas = edicionesPorRestaurante[restauranteId] || { ids: [], nombres: [] };
      
      return {
        id: restauranteId,
        nombre_oficial: restaurante.nombre_oficial,
        rif: restaurante.rif,
        nombre_mostrar: restaurante.nombre_mostrar,
        ventas_promedio:
          restaurante.ventas_promedio != null && restaurante.ventas_promedio !== ''
            ? Number(restaurante.ventas_promedio)
            : null,
        comprobante_reporte_ventas_obligatorio:
          restaurante.comprobante_reporte_ventas_obligatorio !== false,
        breve_resena: restaurante.breve_resena,
        slogan: restaurante.slogan || '',
        notas: restaurante.notas,
        representante: restaurante.representante,
        ciudad_id: restaurante.ciudad_id?.toString(),
        ciudad_nombre: restaurante.ciudades?.nombre,
        email: restaurante.email,
        telefono: restaurante.telefono,
        instagram: restaurante.instagram || '',
        logo: restaurante.imagen_principal,
        logos: parseJSONSafely(restaurante.logos, []),
        logo_seleccionado_index: restaurante.logo_seleccionado_index !== undefined && restaurante.logo_seleccionado_index !== null ? restaurante.logo_seleccionado_index : undefined,
        fachadas: parseJSONSafely(restaurante.fachadas, []),
        sede_ubicacion_corta: restaurante.direccion,
        direccion_completa: restaurante.direccion_completa || '',
        ubicacion_gps: restaurante.ubicacion_gps || '',
        sede_numero_mesas: restaurante.sede_numero_mesas || undefined,
        sede_horario: restaurante.dias_abierto,
        sedes: parseJSONSafely(restaurante.sedes, []),
        latitud_sede_principal: restaurante.latitud_sede_principal ?? null,
        longitud_sede_principal: restaurante.longitud_sede_principal ?? null,
        propuestas: restaurante.propuestas || '',
        premios_obtenidos: restaurante.premios_obtenidos || '',
        fecha_creacion: restaurante.fecha_creacion,
        ediciones_ids: edicionesRelacionadas.ids,
        ediciones_nombres: edicionesRelacionadas.nombres,
        codigo_qr: restaurante.codigo_qr != null ? String(restaurante.codigo_qr) : undefined,
        codigo_cdash:
          restaurante.codigo_cdash != null && String(restaurante.codigo_cdash).trim() !== ''
            ? String(restaurante.codigo_cdash).trim().toUpperCase()
            : undefined,
        bypass_ubicacion: restaurante.bypass_ubicacion === true,
        bypass_rango_restaurante: restaurante.bypass_rango_restaurante === true,
        bypass_votacion: restaurante.bypass_votacion === true,
      };
    });
  } catch (error) {
    console.error('Error obteniendo restaurantes:', error);
    throw error;
  }
};

async function mapRestauranteDbRowConEdiciones(restaurante: any): Promise<Restaurante | null> {
  if (!restaurante) return null;

  const { data: edicionesData } = await supabase
    .from('restaurantes_ediciones')
    .select(
      `ediciones(id, nombre, fecha_inicio, fecha_fin, comprobante_reporte_ventas_obligatorio, precio_propuesta)`
    )
    .eq('restaurante_id', restaurante.id);

  const filasEd = (edicionesData ?? [])
    .map((row: any) => {
      const rawPrecio = row?.ediciones?.precio_propuesta;
      let precio: number | null = null;
      if (rawPrecio != null && rawPrecio !== '') {
        const n = typeof rawPrecio === 'number' ? rawPrecio : Number(rawPrecio);
        if (Number.isFinite(n) && n >= 0) precio = Math.round(n * 100) / 100;
      }
      return {
        id: row?.ediciones?.id != null ? String(row.ediciones.id) : '',
        nombre: row?.ediciones?.nombre != null ? String(row.ediciones.nombre) : '',
        fecha_inicio: row?.ediciones?.fecha_inicio != null ? String(row.ediciones.fecha_inicio) : '',
        fecha_fin: row?.ediciones?.fecha_fin != null ? String(row.ediciones.fecha_fin) : '',
        comprobante_reporte_ventas_obligatorio:
          row?.ediciones?.comprobante_reporte_ventas_obligatorio === false ? false : true,
        precio_propuesta: precio,
      };
    })
    .filter((e) => e.id && e.nombre)
    .sort((a, b) => {
      const ta = a.fecha_inicio ? new Date(a.fecha_inicio).getTime() : 0;
      const tb = b.fecha_inicio ? new Date(b.fecha_inicio).getTime() : 0;
      if (tb !== ta) return tb - ta;
      return parseInt(b.id, 10) - parseInt(a.id, 10);
    });

  return {
    id: restaurante.id.toString(),
    nombre_oficial: restaurante.nombre_oficial,
    rif: restaurante.rif,
    nombre_mostrar: restaurante.nombre_mostrar,
    ventas_promedio:
      restaurante.ventas_promedio != null && restaurante.ventas_promedio !== ''
        ? Number(restaurante.ventas_promedio)
        : null,
    comprobante_reporte_ventas_obligatorio:
      restaurante.comprobante_reporte_ventas_obligatorio !== false,
    breve_resena: restaurante.breve_resena,
    slogan: restaurante.slogan || '',
    notas: restaurante.notas,
    representante: restaurante.representante,
    ciudad_id: restaurante.ciudad_id?.toString(),
    ciudad_nombre: restaurante.ciudades?.nombre,
    email: restaurante.email,
    telefono: restaurante.telefono,
    instagram: restaurante.instagram || '',
    logo: restaurante.imagen_principal,
    logos: parseJSONSafely(restaurante.logos, []),
    logo_seleccionado_index:
      restaurante.logo_seleccionado_index !== undefined && restaurante.logo_seleccionado_index !== null
        ? restaurante.logo_seleccionado_index
        : undefined,
    fachadas: parseJSONSafely(restaurante.fachadas, []),
    sede_ubicacion_corta: restaurante.direccion,
    direccion_completa: restaurante.direccion_completa || '',
    ubicacion_gps: restaurante.ubicacion_gps || '',
    sede_numero_mesas: restaurante.sede_numero_mesas || undefined,
    sede_horario: restaurante.dias_abierto,
    sedes: parseJSONSafely(restaurante.sedes, []),
    latitud_sede_principal: restaurante.latitud_sede_principal ?? null,
    longitud_sede_principal: restaurante.longitud_sede_principal ?? null,
    propuestas: restaurante.propuestas || '',
    premios_obtenidos: restaurante.premios_obtenidos || '',
    fecha_creacion: restaurante.fecha_creacion,
    ediciones_ids: filasEd.map((e) => e.id),
    ediciones_nombres: filasEd.map((e) => e.nombre),
    ediciones_fecha_inicio: filasEd.map((e) => e.fecha_inicio),
    ediciones_fecha_fin: filasEd.map((e) => e.fecha_fin),
    ediciones_comprobante_reporte_ventas_obligatorio: filasEd.map((e) => e.comprobante_reporte_ventas_obligatorio),
    ediciones_precio_propuesta: filasEd.map((e) => e.precio_propuesta),
    codigo_qr: restaurante.codigo_qr != null ? String(restaurante.codigo_qr) : undefined,
    codigo_cdash:
      restaurante.codigo_cdash != null && String(restaurante.codigo_cdash).trim() !== ''
        ? String(restaurante.codigo_cdash).trim().toUpperCase()
        : undefined,
    bypass_ubicacion: restaurante.bypass_ubicacion === true,
    bypass_rango_restaurante: restaurante.bypass_rango_restaurante === true,
    bypass_votacion: restaurante.bypass_votacion === true,
  };
}

/** Restaurante por UUID de QR público (columna `codigo_qr`). */
export const getRestauranteByCodigoQr = async (codigoQr: string): Promise<Restaurante | null> => {
  try {
    const clean = String(codigoQr).trim();
    if (!clean) return null;

    const uuidRe = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    if (!uuidRe.test(clean)) return null;

    const { data: restaurante, error } = await supabase
      .from('restaurantes')
      .select(`
        *,
        ciudades(id, nombre)
      `)
      .eq('codigo_qr', clean)
      .maybeSingle();

    if (error || !restaurante) return null;
    return mapRestauranteDbRowConEdiciones(restaurante);
  } catch (e) {
    console.error('Error getRestauranteByCodigoQr:', e);
    return null;
  }
};

/**
 * Restaurante por id. Permite refrescar una sola fila después de guardar, en
 * vez de volver a pedir la lista completa.
 */
export const getRestaurantePorId = async (id: string): Promise<Restaurante | null> => {
  try {
    const rid = parseInt(String(id), 10);
    if (Number.isNaN(rid)) return null;

    const { data: restaurante, error } = await supabase
      .from('restaurantes')
      .select(`
        *,
        ciudades(id, nombre)
      `)
      .eq('id', rid)
      .maybeSingle();

    if (error || !restaurante) return null;
    return mapRestauranteDbRowConEdiciones(restaurante);
  } catch (e) {
    console.error('Error getRestaurantePorId:', e);
    return null;
  }
};

/** Restaurante por código portal /cdash (8 caracteres alfanuméricos, `codigo_cdash`). */
export const getRestaurantePorCodigoCdash = async (codigoCdash: string): Promise<Restaurante | null> => {
  try {
    const clean = String(codigoCdash).trim().toUpperCase();
    if (!/^[A-Z0-9]{8}$/.test(clean)) return null;

    const { data: restaurante, error } = await supabase
      .from('restaurantes')
      .select(`
        *,
        ciudades(id, nombre)
      `)
      .eq('codigo_cdash', clean)
      .maybeSingle();

    if (error || !restaurante) return null;
    return mapRestauranteDbRowConEdiciones(restaurante);
  } catch (e) {
    console.error('Error getRestaurantePorCodigoCdash:', e);
    return null;
  }
};

// Crear nuevo restaurante
export const addRestaurante = async (restauranteData: {
  nombre_oficial: string;
  rif?: string;
  nombre_mostrar: string;
  ventas_promedio?: number | null;
  comprobante_reporte_ventas_obligatorio?: boolean;
  breve_resena?: string;
  slogan?: string;
  notas?: string;
  representante?: string;
  ciudad_id?: string;
  email?: string;
  telefono?: string;
  instagram?: string;
  logo?: string;
  logos?: Array<{ url: string; id: string }>;
  logo_seleccionado_index?: number;
  fachadas?: Array<{ url: string; id: string }>;
  sede_ubicacion_corta?: string;
  direccion_completa?: string;
  ubicacion_gps?: string;
  sede_numero_mesas?: number;
  sede_horario?: string;
  sedes?: Sede[];
  latitud_sede_principal?: number | null;
  longitud_sede_principal?: number | null;
  propuestas?: string;
  ediciones?: string;
  ediciones_ids?: string[];
  premios_obtenidos?: string;
  bypass_ubicacion?: boolean;
  bypass_rango_restaurante?: boolean;
}): Promise<Restaurante> => {
  try {
    // Mapear campos del frontend a campos reales de la tabla
    const {
      nombre_oficial,
      rif,
      nombre_mostrar,
      ventas_promedio,
      comprobante_reporte_ventas_obligatorio,
      breve_resena,
      slogan,
      notas,
      representante,
      ciudad_id,
      email,
      telefono,
      instagram,
      logo, // se mapea a imagen_principal
      logos,
      logo_seleccionado_index,
      fachadas,
      sede_ubicacion_corta, // se mapea a direccion
      direccion_completa,
      ubicacion_gps,
      sede_numero_mesas,
      sede_horario, // se procesa para horarios
      sedes,
      latitud_sede_principal,
      longitud_sede_principal,
      propuestas,
      ediciones,
      ediciones_ids,
      premios_obtenidos,
      bypass_ubicacion,
      bypass_rango_restaurante,
    } = restauranteData;

    // Procesar horarios si existen
    let dias_abierto = null;
    
    if (sede_horario) {
      // Usar el horario completo sin truncar
      dias_abierto = sede_horario;
    }

    // Preparar datos para Supabase (todos los campos que existen)
    // Asegurar que direccion_completa siempre se envíe, incluso si está vacío
    const direccionCompletaValue = direccion_completa !== undefined && direccion_completa !== null 
      ? String(direccion_completa).trim() 
      : '';
    
    const datosParaInsertar = {
      nombre_oficial,
      rif,
      nombre_mostrar,
      ventas_promedio:
        ventas_promedio !== undefined && ventas_promedio !== null && Number.isFinite(Number(ventas_promedio))
          ? Math.trunc(Number(ventas_promedio))
          : null,
      comprobante_reporte_ventas_obligatorio: comprobante_reporte_ventas_obligatorio !== false,
      breve_resena,
      slogan,
      notas,
      representante,
      ciudad_id: ciudad_id ? parseInt(ciudad_id) : null,
      email,
      telefono,
      instagram,
      direccion: sede_ubicacion_corta,
      direccion_completa: direccionCompletaValue,
      ubicacion_gps,
      sede_numero_mesas: sede_numero_mesas || null,
      dias_abierto,
      imagen_principal: logo,
      logos: logos ? JSON.stringify(logos) : null,
      logo_seleccionado_index: logo_seleccionado_index !== undefined && logo_seleccionado_index !== null ? logo_seleccionado_index : null,
      fachadas: fachadas ? JSON.stringify(fachadas) : null,
      sedes: sedes ? JSON.stringify(sedes) : null,
      latitud_sede_principal: latitud_sede_principal ?? null,
      longitud_sede_principal: longitud_sede_principal ?? null,
      propuestas,
      ediciones,
      premios_obtenidos,
      bypass_ubicacion: bypass_ubicacion === true,
      bypass_rango_restaurante: bypass_rango_restaurante === true,
      estado: 'activo'
    };
    
    console.log('📝 direccion_completa que se enviará a Supabase (addRestaurante):', direccionCompletaValue);
    console.log('📝 tipo de direccion_completa:', typeof direccionCompletaValue);

    let { data, error } = await supabase
      .from('restaurantes')
      .insert([datosParaInsertar])
      .select(`
        *,
        ciudades(id, nombre)
      `)
      .single();

    if (error && isMissingSedePrincipalCoordsColumnsError(error)) {
      console.warn(
        '[restaurantes] Sin columnas latitud_sede_principal/longitud_sede_principal en Supabase. ' +
          'Aplica la migración 20260404120000_restaurantes_sede_principal_coords.sql. ' +
          'Reintentando inserción sin esas columnas.'
      );
      const retryInsert = omitSedePrincipalCoords(datosParaInsertar as Record<string, unknown>);
      const second = await supabase
        .from('restaurantes')
        .insert([retryInsert])
        .select(`
        *,
        ciudades(id, nombre)
      `)
        .single();
      data = second.data;
      error = second.error;
    }

    if (error) {
      // Mejorar el mensaje de error para errores de secuencia
      if (error.code === '23505' && error.message?.includes('restaurantes_pkey')) {
        const betterError = new Error(
          'Error de secuencia en la base de datos. La secuencia de auto-incremento está desincronizada.\n\n' +
          'Por favor, ejecuta el siguiente comando SQL en el SQL Editor de Supabase:\n\n' +
          "SELECT setval('restaurantes_id_seq', COALESCE((SELECT MAX(id) FROM restaurantes), 0) + 1, false);\n\n" +
          'O ejecuta el script FIX_RESTAURANTES_SEQUENCE.sql que se encuentra en la raíz del proyecto.'
        );
        // Preservar el código de error original
        (betterError as any).code = error.code;
        throw betterError;
      }
      
      throw error;
    }

    // Actualizar relaciones con ediciones
    if (ediciones_ids !== undefined && ediciones_ids.length > 0) {
      console.log('🔍 Creando relaciones restaurante-ediciones con ediciones_ids:', ediciones_ids);
      
      const edicionesRelaciones = ediciones_ids.map(edicion_id => ({
        restaurante_id: data.id,
        edicion_id: parseInt(edicion_id)
      }));
      
      console.log('🔍 Insertando relaciones:', edicionesRelaciones);
      const { error: errorEdiciones } = await supabase
        .from('restaurantes_ediciones')
        .insert(edicionesRelaciones);
      
      if (errorEdiciones) {
        console.error('❌ Error insertando relaciones con ediciones:', errorEdiciones);
      } else {
        console.log('✅ Relaciones con ediciones insertadas correctamente');
      }
    }

    // Obtener ediciones relacionadas para retornar
    const { data: edicionesData } = await supabase
      .from('restaurantes_ediciones')
      .select(`
        ediciones(id, nombre)
      `)
      .eq('restaurante_id', data.id);

    return {
      id: data.id.toString(),
      nombre_oficial: data.nombre_oficial,
      rif: data.rif,
      nombre_mostrar: data.nombre_mostrar,
      ventas_promedio:
        data.ventas_promedio != null && data.ventas_promedio !== ''
          ? Number(data.ventas_promedio)
          : null,
      comprobante_reporte_ventas_obligatorio:
        data.comprobante_reporte_ventas_obligatorio !== false,
      breve_resena: data.breve_resena,
      slogan: data.slogan || '',
      notas: data.notas,
      representante: data.representante,
      ciudad_id: data.ciudad_id?.toString(),
      ciudad_nombre: data.ciudades?.nombre,
      email: data.email,
      telefono: data.telefono,
      instagram: data.instagram || '',
      logo: data.imagen_principal,
      logos: parseJSONSafely(data.logos, []),
      logo_seleccionado_index: data.logo_seleccionado_index !== undefined && data.logo_seleccionado_index !== null ? data.logo_seleccionado_index : undefined,
      sede_ubicacion_corta: data.direccion,
      direccion_completa: data.direccion_completa || '',
      ubicacion_gps: data.ubicacion_gps || '',
      sede_numero_mesas: data.sede_numero_mesas || undefined,
      sede_horario: data.dias_abierto,
      sedes: parseJSONSafely(data.sedes, []),
      latitud_sede_principal: data.latitud_sede_principal ?? null,
      longitud_sede_principal: data.longitud_sede_principal ?? null,
      propuestas: data.propuestas || '',
      ediciones: data.ediciones || '',
      premios_obtenidos: data.premios_obtenidos || '',
      fecha_creacion: data.fecha_creacion,
      ediciones_ids: edicionesData?.map((e: any) => e.ediciones.id.toString()) || [],
      ediciones_nombres: edicionesData?.map((e: any) => e.ediciones.nombre) || [],
      codigo_qr: data.codigo_qr != null ? String(data.codigo_qr) : undefined,
      codigo_cdash:
        data.codigo_cdash != null && String(data.codigo_cdash).trim() !== ''
          ? String(data.codigo_cdash).trim().toUpperCase()
          : undefined,
      bypass_ubicacion: data.bypass_ubicacion === true,
      bypass_rango_restaurante: data.bypass_rango_restaurante === true,
      bypass_votacion: data.bypass_votacion === true,
    };
  } catch (error: any) {
    console.error('Error agregando restaurante:', error);
    
    // Mejorar el mensaje de error para el usuario
    if (error.message && error.message.includes('secuencia')) {
      throw error; // Ya tiene un mensaje claro
    } else if (error.code === '23505') {
      throw new Error(
        'Error de secuencia en la base de datos. Por favor, ejecuta el siguiente comando SQL en Supabase:\n\n' +
        "SELECT setval('restaurantes_id_seq', COALESCE((SELECT MAX(id) FROM restaurantes), 0) + 1, false);"
      );
    }
    
    throw error;
  }
};

// Actualizar restaurante
export const updateRestaurante = async (id: string, restauranteData: {
  nombre_oficial: string;
  rif?: string;
  nombre_mostrar: string;
  ventas_promedio?: number | null;
  comprobante_reporte_ventas_obligatorio?: boolean;
  breve_resena?: string;
  slogan?: string;
  notas?: string;
  representante?: string;
  ciudad_id?: string;
  email?: string;
  telefono?: string;
  instagram?: string;
  logo?: string;
  logos?: Array<{ url: string; id: string }>;
  logo_seleccionado_index?: number;
  fachadas?: Array<{ url: string; id: string }>;
  sede_ubicacion_corta?: string;
  direccion_completa?: string;
  ubicacion_gps?: string;
  sede_numero_mesas?: number;
  sede_horario?: string;
  sedes?: Sede[];
  latitud_sede_principal?: number | null;
  longitud_sede_principal?: number | null;
  propuestas?: string;
  ediciones?: string;
  premios_obtenidos?: string;
  ediciones_ids?: string[];
  bypass_ubicacion?: boolean;
  bypass_rango_restaurante?: boolean;
  bypass_votacion?: boolean;
}): Promise<{
  restaurante: Restaurante;
  coordsSedePrincipalNoPersistidas: boolean;
  syncSedes?: { propuestasRevisadas: number; sedesAgregadas: number; sedesActualizadas: number };
}> => {
  try {
    // Mapear campos del frontend a campos reales de la tabla
    const {
      nombre_oficial,
      rif,
      nombre_mostrar,
      ventas_promedio,
      comprobante_reporte_ventas_obligatorio,
      breve_resena,
      slogan,
      notas,
      representante,
      ciudad_id,
      email,
      telefono,
      instagram,
      logo, // se mapea a imagen_principal
      logos,
      logo_seleccionado_index,
      fachadas,
      sede_ubicacion_corta, // se mapea a direccion
      direccion_completa,
      ubicacion_gps,
      sede_numero_mesas,
      sede_horario, // se procesa para horarios
      sedes,
      latitud_sede_principal,
      longitud_sede_principal,
      propuestas,
      premios_obtenidos,
      ediciones_ids,
      bypass_ubicacion,
      bypass_rango_restaurante,
      bypass_votacion,
    } = restauranteData;

    // Procesar horarios si existen
    let dias_abierto = null;
    
    if (sede_horario) {
      // Usar el horario completo sin truncar
      dias_abierto = sede_horario;
    }

    // Preparar datos para Supabase (todos los campos que existen)
    // Asegurar que direccion_completa siempre se envíe, incluso si está vacío
    const direccionCompletaValue = direccion_completa !== undefined && direccion_completa !== null 
      ? String(direccion_completa).trim() 
      : '';
    
    const datosParaActualizar = {
      nombre_oficial,
      rif,
      nombre_mostrar,
      ...(ventas_promedio !== undefined
        ? {
            ventas_promedio:
              ventas_promedio !== null && Number.isFinite(Number(ventas_promedio))
                ? Math.trunc(Number(ventas_promedio))
                : null,
          }
        : {}),
      comprobante_reporte_ventas_obligatorio: comprobante_reporte_ventas_obligatorio !== false,
      breve_resena,
      slogan,
      notas,
      representante,
      ciudad_id: ciudad_id ? parseInt(ciudad_id) : null,
      email,
      telefono,
      instagram,
      direccion: sede_ubicacion_corta,
      direccion_completa: direccionCompletaValue,
      ubicacion_gps,
      sede_numero_mesas: sede_numero_mesas || null,
      dias_abierto,
      imagen_principal: logo,
      logos: logos ? JSON.stringify(logos) : null,
      logo_seleccionado_index: logo_seleccionado_index !== undefined && logo_seleccionado_index !== null ? logo_seleccionado_index : null,
      fachadas: fachadas ? JSON.stringify(fachadas) : null,
      sedes: sedes ? JSON.stringify(sedes) : null,
      latitud_sede_principal: latitud_sede_principal ?? null,
      longitud_sede_principal: longitud_sede_principal ?? null,
      propuestas,
      premios_obtenidos,
      ...(bypass_ubicacion !== undefined ? { bypass_ubicacion: bypass_ubicacion === true } : {}),
      ...(bypass_rango_restaurante !== undefined
        ? { bypass_rango_restaurante: bypass_rango_restaurante === true }
        : {}),
      ...(bypass_votacion !== undefined ? { bypass_votacion: bypass_votacion === true } : {}),
      estado: 'activo'
    };
    
    console.log('📝 direccion_completa que se enviará a Supabase (updateRestaurante):', direccionCompletaValue);
    console.log('📝 tipo de direccion_completa:', typeof direccionCompletaValue);

    let { data, error } = await supabase
      .from('restaurantes')
      .update(datosParaActualizar)
      .eq('id', id)
      .select(`
        *,
        ciudades(id, nombre)
      `)
      .single();

    let coordsSedePrincipalNoPersistidas = false;
    if (error && isMissingSedePrincipalCoordsColumnsError(error)) {
      const teniaCoordsEnPayload =
        datosParaActualizar.latitud_sede_principal != null ||
        datosParaActualizar.longitud_sede_principal != null;
      coordsSedePrincipalNoPersistidas = teniaCoordsEnPayload;
      console.warn(
        '[restaurantes] Sin columnas latitud_sede_principal/longitud_sede_principal en Supabase. ' +
          'Aplica la migración 20260404120000_restaurantes_sede_principal_coords.sql. ' +
          'Reintentando actualización sin esas columnas.'
      );
      const retryUpdate = omitSedePrincipalCoords(datosParaActualizar as Record<string, unknown>);
      const second = await supabase
        .from('restaurantes')
        .update(retryUpdate)
        .eq('id', id)
        .select(`
        *,
        ciudades(id, nombre)
      `)
        .single();
      data = second.data;
      error = second.error;
    }

    if (error) throw error;

    // Actualizar relaciones con ediciones
    if (ediciones_ids !== undefined) {
      console.log('🔍 Actualizando relaciones restaurante-ediciones con ediciones_ids:', ediciones_ids);
      
      // Eliminar relaciones existentes
      const { error: deleteError } = await supabase
        .from('restaurantes_ediciones')
        .delete()
        .eq('restaurante_id', parseInt(id));
      
      if (deleteError) {
        console.error('❌ Error eliminando relaciones existentes con ediciones:', deleteError);
      } else {
        console.log('✅ Relaciones existentes eliminadas correctamente');
      }
      
      // Insertar nuevas relaciones
      if (ediciones_ids.length > 0) {
        const edicionesRelaciones = ediciones_ids.map(edicion_id => ({
          restaurante_id: parseInt(id),
          edicion_id: parseInt(edicion_id)
        }));
        
        console.log('🔍 Insertando nuevas relaciones:', edicionesRelaciones);
        const { error: errorEdiciones } = await supabase
          .from('restaurantes_ediciones')
          .insert(edicionesRelaciones);
        
        if (errorEdiciones) {
          console.error('❌ Error insertando relaciones con ediciones:', errorEdiciones);
        } else {
          console.log('✅ Relaciones con ediciones insertadas correctamente');
        }
      } else {
        console.log('ℹ️ No hay ediciones para vincular (array vacío)');
      }
    }

    // Obtener ediciones relacionadas para retornar
    const { data: edicionesData } = await supabase
      .from('restaurantes_ediciones')
      .select(`
        ediciones(id, nombre)
      `)
      .eq('restaurante_id', parseInt(id));

    let syncSedes: { propuestasRevisadas: number; sedesAgregadas: number; sedesActualizadas: number } | undefined;
    try {
      syncSedes = await syncSedesRestauranteAPropuestas(id, {
        sede_ubicacion_corta: data.direccion,
        direccion_completa: data.direccion_completa || '',
        sede_horario: data.dias_abierto || '',
        ubicacion_gps: data.ubicacion_gps || '',
        sedes: parseJSONSafely(data.sedes, []),
      });
      if (syncSedes.sedesAgregadas > 0 || syncSedes.sedesActualizadas > 0) {
        console.log(
          `[syncSedes] Restaurante ${id}: +${syncSedes.sedesAgregadas} sedes nuevas, ` +
            `${syncSedes.sedesActualizadas} actualizadas en ${syncSedes.propuestasRevisadas} propuestas`
        );
      }
    } catch (syncError) {
      // No fallar el guardado del restaurante si la sync de propuestas falla
      console.warn('[syncSedes] Error sincronizando sedes a propuestas:', syncError);
    }

    return {
      restaurante: {
        id: data.id.toString(),
        nombre_oficial: data.nombre_oficial,
        rif: data.rif,
        nombre_mostrar: data.nombre_mostrar,
        ventas_promedio:
          data.ventas_promedio != null && data.ventas_promedio !== ''
            ? Number(data.ventas_promedio)
            : null,
        comprobante_reporte_ventas_obligatorio:
          data.comprobante_reporte_ventas_obligatorio !== false,
        breve_resena: data.breve_resena,
        slogan: data.slogan || '',
        notas: data.notas,
        representante: data.representante,
        ciudad_id: data.ciudad_id?.toString(),
        ciudad_nombre: data.ciudades?.nombre,
        email: data.email,
        telefono: data.telefono,
        instagram: data.instagram || '',
        logo: data.imagen_principal,
        logos: parseJSONSafely(data.logos, []),
        logo_seleccionado_index:
          data.logo_seleccionado_index !== undefined && data.logo_seleccionado_index !== null
            ? data.logo_seleccionado_index
            : undefined,
        fachadas: parseJSONSafely(data.fachadas, []),
        sede_ubicacion_corta: data.direccion,
        direccion_completa: data.direccion_completa || '',
        ubicacion_gps: data.ubicacion_gps || '',
        sede_numero_mesas: data.sede_numero_mesas || undefined,
        sede_horario: data.dias_abierto,
        sedes: parseJSONSafely(data.sedes, []),
        latitud_sede_principal: data.latitud_sede_principal ?? null,
        longitud_sede_principal: data.longitud_sede_principal ?? null,
        propuestas: data.propuestas || '',
        premios_obtenidos: data.premios_obtenidos || '',
        fecha_creacion: data.fecha_creacion,
        ediciones_ids: edicionesData?.map((e: any) => e.ediciones.id.toString()) || [],
        ediciones_nombres: edicionesData?.map((e: any) => e.ediciones.nombre) || [],
        codigo_qr: data.codigo_qr != null ? String(data.codigo_qr) : undefined,
        codigo_cdash:
          data.codigo_cdash != null && String(data.codigo_cdash).trim() !== ''
            ? String(data.codigo_cdash).trim().toUpperCase()
            : undefined,
        bypass_ubicacion: data.bypass_ubicacion === true,
        bypass_rango_restaurante: data.bypass_rango_restaurante === true,
        bypass_votacion: data.bypass_votacion === true,
      },
      coordsSedePrincipalNoPersistidas,
      syncSedes,
    };
  } catch (error) {
    console.error('Error actualizando restaurante:', error);
    throw error;
  }
};

/** Actualiza solo los flags de bypass de votación (tabla restaurantes). */
export const updateRestauranteBypassFlags = async (
  id: string,
  flags: {
    bypass_ubicacion?: boolean;
    bypass_rango_restaurante?: boolean;
    bypass_votacion?: boolean;
  }
): Promise<void> => {
  const datos: Record<string, boolean> = {};
  if (flags.bypass_ubicacion !== undefined) {
    datos.bypass_ubicacion = flags.bypass_ubicacion === true;
  }
  if (flags.bypass_rango_restaurante !== undefined) {
    datos.bypass_rango_restaurante = flags.bypass_rango_restaurante === true;
  }
  if (flags.bypass_votacion !== undefined) {
    datos.bypass_votacion = flags.bypass_votacion === true;
  }
  if (Object.keys(datos).length === 0) return;

  const rid = parseInt(String(id), 10);
  if (Number.isNaN(rid)) {
    throw new Error('ID de restaurante no válido');
  }

  const { data, error } = await supabase
    .from('restaurantes')
    .update(datos)
    .eq('id', rid)
    .select('id');
  if (error) throw error;
  if (!data?.length) {
    throw new Error(
      'No se actualizó el restaurante. Verifica permisos o que la migración de bypass en restaurantes esté aplicada.'
    );
  }

  if (flags.bypass_rango_restaurante !== undefined) {
    const { error: propError } = await supabase
      .from('propuestas')
      .update({ bypass_rango_restaurante: flags.bypass_rango_restaurante === true })
      .eq('restaurante_id', rid);
    if (propError) {
      console.warn('No se pudo sincronizar bypass_rango en propuestas:', propError);
    }
  }
};

/** Activa/desactiva bypass de rango en varios restaurantes (y sincroniza propuestas). */
export const updateBypassRangoRestaurantes = async (
  restauranteIds: string[],
  bypass_rango_restaurante: boolean
): Promise<void> => {
  const ids = [
    ...new Set(
      restauranteIds
        .map((id) => parseInt(String(id), 10))
        .filter((id) => !Number.isNaN(id))
    ),
  ];
  if (ids.length === 0) return;

  const value = bypass_rango_restaurante === true;
  const { data, error } = await supabase
    .from('restaurantes')
    .update({ bypass_rango_restaurante: value })
    .in('id', ids)
    .select('id');
  if (error) throw error;
  if (!data?.length) {
    throw new Error(
      'No se actualizaron restaurantes. Verifica permisos o que la migración de bypass esté aplicada.'
    );
  }

  const { error: propError } = await supabase
    .from('propuestas')
    .update({ bypass_rango_restaurante: value })
    .in('restaurante_id', ids);
  if (propError) {
    console.warn('No se pudo sincronizar bypass_rango en propuestas:', propError);
  }
};

// Eliminar restaurante
export const deleteRestaurante = async (id: string): Promise<void> => {
  try {
    const { error } = await supabase
      .from('restaurantes')
      .delete()
      .eq('id', id);

    if (error) throw error;
  } catch (error) {
    console.error('Error eliminando restaurante:', error);
    throw error;
  }
};

// Buscar restaurantes
// Si ciudades_ids está presente, filtra restaurantes por esas ciudades (para admins)
export const searchRestaurantes = async (query: string, ciudades_ids?: string[]): Promise<Restaurante[]> => {
  try {
    let queryBuilder = supabase
      .from('restaurantes')
      .select(`
        *,
        ciudades(id, nombre)
      `)
      .or(`nombre_oficial.ilike.%${query}%,nombre_mostrar.ilike.%${query}%,breve_resena.ilike.%${query}%`);

    // Si se proporcionan ciudades_ids, filtrar por ellas (para admins)
    if (ciudades_ids && ciudades_ids.length > 0) {
      const ciudadIdsInt = ciudades_ids.map(id => parseInt(id)).filter(id => !isNaN(id));
      if (ciudadIdsInt.length > 0) {
        queryBuilder = queryBuilder.in('ciudad_id', ciudadIdsInt);
      }
    }

    const { data, error } = await queryBuilder.order('fecha_creacion', { ascending: false });

    if (error) throw error;

    return data.map((restaurante: any) => {
      return {
      id: restaurante.id.toString(),
      nombre_oficial: restaurante.nombre_oficial,
      rif: restaurante.rif,
      nombre_mostrar: restaurante.nombre_mostrar,
      ventas_promedio:
        restaurante.ventas_promedio != null && restaurante.ventas_promedio !== ''
          ? Number(restaurante.ventas_promedio)
          : null,
      comprobante_reporte_ventas_obligatorio:
        restaurante.comprobante_reporte_ventas_obligatorio !== false,
      breve_resena: restaurante.breve_resena,
      slogan: restaurante.slogan || '',
      representante: restaurante.representante,
      numero_mesas: restaurante.numero_mesas,
      ciudad_id: restaurante.ciudad_id?.toString(),
        ciudad_nombre: restaurante.ciudades?.nombre,
      email: restaurante.email,
      telefono: restaurante.telefono,
      instagram: restaurante.instagram || '',
      logo: restaurante.imagen_principal,
      sede_ubicacion_corta: restaurante.direccion,
      direccion_completa: restaurante.direccion_completa || '',
      ubicacion_gps: restaurante.ubicacion_gps || '',
      sede_horario: restaurante.dias_abierto,
      sedes: parseJSONSafely(restaurante.sedes, []),
      latitud_sede_principal: restaurante.latitud_sede_principal ?? null,
      longitud_sede_principal: restaurante.longitud_sede_principal ?? null,
      propuestas: restaurante.propuestas || '',
      ediciones: restaurante.ediciones || '',
      premios_obtenidos: restaurante.premios_obtenidos || '',
      fecha_creacion: restaurante.fecha_creacion,
      codigo_qr: restaurante.codigo_qr != null ? String(restaurante.codigo_qr) : undefined,
      codigo_cdash:
        restaurante.codigo_cdash != null && String(restaurante.codigo_cdash).trim() !== ''
          ? String(restaurante.codigo_cdash).trim().toUpperCase()
          : undefined,
      };
    });
  } catch (error) {
    console.error('Error buscando restaurantes:', error);
    throw error;
  }
};

// ========================================
// FUNCIONES PARA EDICIONES
// ========================================

// Obtener ediciÃ³n por ID
export const getEdicionById = async (id: string): Promise<Edicion | null> => {
  try {
    const { data, error } = await supabase
      .from('ediciones')
      .select('*')
      .eq('id', id)
      .single();

    if (error) throw error;

    // Obtener ciudades relacionadas (con publicación por ciudad si la migración está aplicada)
    let { data: ciudadesData, error: ciudadesError } = await supabase
      .from('ediciones_ciudades')
      .select(`
        fecha_publicacion,
        habilitado,
        ciudades(id, nombre, cover_web, cover_mobile)
      `)
      .eq('edicion_id', id);

    if (ciudadesError) {
      const msg = String(ciudadesError.message || '');
      if (
        msg.includes('fecha_publicacion') ||
        msg.includes('habilitado') ||
        (ciudadesError as { code?: string }).code === '42703'
      ) {
        const fallback = await supabase
          .from('ediciones_ciudades')
          .select(`
            ciudades(id, nombre, cover_web, cover_mobile)
          `)
          .eq('edicion_id', id);
        ciudadesData = fallback.data;
        ciudadesError = fallback.error;
      }
    }
    if (ciudadesError) {
      console.error('Error obteniendo ciudades de la edición:', ciudadesError);
    }

    // Obtener patrocinadores relacionados
    const { data: patrocinadoresData } = await supabase
      .from('ediciones_patrocinadores')
      .select(`
        patrocinadores(id, nombre)
      `)
      .eq('edicion_id', id);

    // Obtener restaurantes relacionados
    const { data: restaurantesData } = await supabase
      .from('restaurantes_ediciones')
      .select(`
        restaurantes(id, nombre_mostrar)
      `)
      .eq('edicion_id', id);

    const itemsVotacion = parseItemsVotacion(data.items_votacion);

    const edicionResult = {
      id: data.id,
      nombre: data.nombre,
      nombre_link:
        data.nombre_link != null && String(data.nombre_link).trim() !== ''
          ? String(data.nombre_link).trim()
          : null,
      categoria: data.categoria,
      trofeos: parseEdicionTrofeos(data.trofeos),
      descripcion: data.descripcion,
      fecha_inicio: data.fecha_inicio,
      fecha_fin: data.fecha_fin,
      fecha_anuncio: data.fecha_anuncio ?? null,
      fecha_catalogo: data.fecha_catalogo ?? null,
      logotema: data.logotema,
      portada_web: data.portada_web,
      portada_mobile: data.portada_mobile,
      portada2_web: data.portada2_web,
      portada2_mobile: data.portada2_mobile,
      portada_app: data.portada_app,
      imagen_principal: data.imagen_principal,
      estado: data.estado as EdicionEstado,
      estado_automatico: data.estado_automatico === true,
      modo_prueba: data.modo_prueba === true,
      comprobante_reporte_ventas_obligatorio: data.comprobante_reporte_ventas_obligatorio !== false,
      precio_propuesta: (() => {
        const raw = data.precio_propuesta;
        if (raw == null || raw === '') return null;
        const n = typeof raw === 'number' ? raw : Number(raw);
        return Number.isFinite(n) && n >= 0 ? Math.round(n * 100) / 100 : null;
      })(),
      top3_clasica_peso_ventas:
        typeof data.top3_clasica_peso_ventas === 'number' && Number.isFinite(data.top3_clasica_peso_ventas)
          ? data.top3_clasica_peso_ventas
          : 20,
      top3_clasica_peso_pct_votos_hechos:
        typeof data.top3_clasica_peso_pct_votos_hechos === 'number' &&
        Number.isFinite(data.top3_clasica_peso_pct_votos_hechos)
          ? data.top3_clasica_peso_pct_votos_hechos
          : 20,
      top3_clasica_peso_promedio:
        typeof data.top3_clasica_peso_promedio === 'number' && Number.isFinite(data.top3_clasica_peso_promedio)
          ? data.top3_clasica_peso_promedio
          : 40,
      top3_clasica_peso_pct_perfectos:
        typeof data.top3_clasica_peso_pct_perfectos === 'number' &&
        Number.isFinite(data.top3_clasica_peso_pct_perfectos)
          ? data.top3_clasica_peso_pct_perfectos
          : 20,
      votacion_color_titulo:
        typeof data.votacion_color_titulo === 'string' && data.votacion_color_titulo.trim() !== ''
          ? data.votacion_color_titulo.trim()
          : '#000000',
      votacion_color_subtitulo:
        typeof data.votacion_color_subtitulo === 'string' && data.votacion_color_subtitulo.trim() !== ''
          ? data.votacion_color_subtitulo.trim()
          : '#A7ABB4',
      votacion_color_fondo:
        typeof data.votacion_color_fondo === 'string' && data.votacion_color_fondo.trim() !== ''
          ? data.votacion_color_fondo.trim()
          : '#FFFFFF',
      votacion_color_boton:
        typeof data.votacion_color_boton === 'string' && data.votacion_color_boton.trim() !== ''
          ? data.votacion_color_boton.trim()
          : '#A20000',
      votacion_color_texto_boton:
        typeof data.votacion_color_texto_boton === 'string' &&
        data.votacion_color_texto_boton.trim() !== ''
          ? data.votacion_color_texto_boton.trim()
          : '#FFFFFF',
      votacion_exitoso_color_titulo:
        typeof data.votacion_exitoso_color_titulo === 'string' &&
        data.votacion_exitoso_color_titulo.trim() !== ''
          ? data.votacion_exitoso_color_titulo.trim()
          : '#030213',
      votacion_exitoso_color_subtitulo:
        typeof data.votacion_exitoso_color_subtitulo === 'string' &&
        data.votacion_exitoso_color_subtitulo.trim() !== ''
          ? data.votacion_exitoso_color_subtitulo.trim()
          : '#030213',
      votacion_exitoso_color_fondo:
        typeof data.votacion_exitoso_color_fondo === 'string' &&
        data.votacion_exitoso_color_fondo.trim() !== ''
          ? data.votacion_exitoso_color_fondo.trim()
          : '#F9C22E',
      votacion_exitoso_color_boton:
        typeof data.votacion_exitoso_color_boton === 'string' &&
        data.votacion_exitoso_color_boton.trim() !== ''
          ? data.votacion_exitoso_color_boton.trim()
          : '#C50018',
      votacion_exitoso_color_texto_boton:
        typeof data.votacion_exitoso_color_texto_boton === 'string' &&
        data.votacion_exitoso_color_texto_boton.trim() !== ''
          ? data.votacion_exitoso_color_texto_boton.trim()
          : '#FFFFFF',
      votacion_exitoso_cta_texto:
        typeof data.votacion_exitoso_cta_texto === 'string' &&
        data.votacion_exitoso_cta_texto.trim() !== ''
          ? data.votacion_exitoso_cta_texto.trim()
          : '¡Síguenos!',
      votacion_exitoso_cta_texto_descriptivo:
        typeof data.votacion_exitoso_cta_texto_descriptivo === 'string'
          ? data.votacion_exitoso_cta_texto_descriptivo
          : '',
      votacion_exitoso_cta_link:
        typeof data.votacion_exitoso_cta_link === 'string' &&
        data.votacion_exitoso_cta_link.trim() !== ''
          ? data.votacion_exitoso_cta_link.trim()
          : 'https://www.instagram.com/asgastronomico/',
      votacion_exitoso_cta_icono:
        typeof data.votacion_exitoso_cta_icono === 'string' &&
        data.votacion_exitoso_cta_icono.trim() !== ''
          ? data.votacion_exitoso_cta_icono.trim()
          : 'logo-instagram',
      votacion_fallido_color_titulo:
        typeof data.votacion_fallido_color_titulo === 'string' &&
        data.votacion_fallido_color_titulo.trim() !== ''
          ? data.votacion_fallido_color_titulo.trim()
          : '#030213',
      votacion_fallido_color_subtitulo:
        typeof data.votacion_fallido_color_subtitulo === 'string' &&
        data.votacion_fallido_color_subtitulo.trim() !== ''
          ? data.votacion_fallido_color_subtitulo.trim()
          : '#030213',
      votacion_fallido_color_fondo:
        typeof data.votacion_fallido_color_fondo === 'string' &&
        data.votacion_fallido_color_fondo.trim() !== ''
          ? data.votacion_fallido_color_fondo.trim()
          : '#F9C22E',
      votacion_fallido_color_boton:
        typeof data.votacion_fallido_color_boton === 'string' &&
        data.votacion_fallido_color_boton.trim() !== ''
          ? data.votacion_fallido_color_boton.trim()
          : '#A20000',
      votacion_fallido_color_texto_boton:
        typeof data.votacion_fallido_color_texto_boton === 'string' &&
        data.votacion_fallido_color_texto_boton.trim() !== ''
          ? data.votacion_fallido_color_texto_boton.trim()
          : '#FFFFFF',
      votacion_distancia_minima_metros:
        typeof data.votacion_distancia_minima_metros === 'number' &&
        Number.isFinite(data.votacion_distancia_minima_metros) &&
        data.votacion_distancia_minima_metros > 0
          ? Math.floor(data.votacion_distancia_minima_metros)
          : 500,
      fecha_creacion: data.fecha_creacion,
      items_votacion: itemsVotacion,
      catalogo_banner_colores: parseCatalogoBannerColores(data.catalogo_banner_colores),
      ciudades_ids: ciudadesData?.map((c: any) => c.ciudades.id.toString()) || [],
      ciudades_nombres: ciudadesData?.map((c: any) => c.ciudades.nombre) || [],
      ciudades_cover_web: ciudadesData?.map((c: any) => c.ciudades?.cover_web ?? null) || [],
      ciudades_cover_mobile: ciudadesData?.map((c: any) => c.ciudades?.cover_mobile ?? null) || [],
      ciudades_publicacion:
        ciudadesData?.map((c: any) => {
          const hasPubFields =
            Object.prototype.hasOwnProperty.call(c, 'habilitado') ||
            Object.prototype.hasOwnProperty.call(c, 'fecha_publicacion');
          return {
            ciudad_id: c.ciudades.id.toString(),
            fecha_publicacion: c.fecha_publicacion
              ? String(c.fecha_publicacion).slice(0, 10)
              : null,
            // Sin columnas de publicación (migración pendiente): tratar como habilitado.
            habilitado: hasPubFields ? c.habilitado === true : true,
          };
        }) || [],
      patrocinadores_ids: patrocinadoresData?.map((p: any) => p.patrocinadores.id.toString()) || [],
      patrocinadores_nombres: patrocinadoresData?.map((p: any) => p.patrocinadores.nombre) || [],
      restaurantes_ids: restaurantesData?.map((r: any) => r.restaurantes.id.toString()) || [],
      restaurantes_nombres: restaurantesData?.map((r: any) => r.restaurantes.nombre_mostrar) || [],
    };

    return edicionResult;
  } catch (error) {
    console.error('Error obteniendo ediciÃ³n por ID:', error);
    return null;
  }
};

/** Catálogo público: resuelve `/edicion/:segment` por slug o por id numérico (enlaces antiguos). */
export const resolveEdicionFromRouteParam = async (param: string): Promise<Edicion | null> => {
  const trimmed = param.trim();
  if (!trimmed) return null;
  try {
    const decoded = decodeURIComponent(trimmed);
    if (/^\d+$/.test(decoded)) {
      return getEdicionById(decoded);
    }
    const slug = normalizeEdicionNombreLink(decoded);
    if (!slug) return null;
    const { data, error } = await supabase
      .from('ediciones')
      .select('id')
      .eq('nombre_link', slug)
      .maybeSingle();
    if (error) throw error;
    if (!data?.id) return null;
    return getEdicionById(String(data.id));
  } catch (error) {
    console.error('Error resolviendo edición por nombre de link:', error);
    return null;
  }
};

export async function isEdicionNombreLinkDisponible(
  slug: string,
  excludeEdicionId?: number | string
): Promise<boolean> {
  const normalizado = normalizeEdicionNombreLink(slug);
  if (!isEdicionNombreLinkValido(normalizado)) return false;
  const { data, error } = await supabase
    .from('ediciones')
    .select('id')
    .eq('nombre_link', normalizado)
    .maybeSingle();
  if (error) throw error;
  if (!data?.id) return true;
  if (excludeEdicionId != null && String(data.id) === String(excludeEdicionId)) return true;
  return false;
}

// Obtener todas las ediciones
export const getEdiciones = async (): Promise<Edicion[]> => {
  try {
    const { data, error } = await supabase
      .from('ediciones')
      .select('*')
      .order('fecha_creacion', { ascending: false });

    if (error) throw error;

    // Obtener todas las ediciones con sus relaciones
    const ediciones = await Promise.all(
      data.map(async (edicion: any) => {
        return await getEdicionById(String(edicion.id));
      })
    );

    return ediciones.filter((edicion): edicion is Edicion => edicion !== null);
  } catch (error) {
    console.error('Error obteniendo ediciones:', error);
    return [];
  }
};

function parseEdicionTrofeos(raw: unknown): string[] {
  if (raw == null) return [];
  let arr: unknown[] = [];
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) arr = parsed;
    } catch {
      return [];
    }
  } else if (Array.isArray(raw)) {
    arr = raw;
  } else {
    return [];
  }
  const seen = new Set<string>();
  const out: string[] = [];
  for (const item of arr) {
    const t = String(item ?? '').trim();
    if (!t) continue;
    const key = t.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(t);
  }
  return out;
}

function buildEdicionesCiudadesRows(
  edicionId: number | string,
  ciudades_ids: string[],
  ciudades_publicacion?: EdicionCiudadPublicacion[]
): Array<{
  edicion_id: number;
  ciudad_id: number;
  fecha_publicacion: string | null;
  habilitado: boolean;
}> {
  const byId = new Map(
    (ciudades_publicacion ?? []).map((c) => [String(c.ciudad_id), c])
  );
  return ciudades_ids.map((ciudad_id) => {
    const cfg = byId.get(String(ciudad_id));
    const fecha =
      cfg?.fecha_publicacion && String(cfg.fecha_publicacion).trim() !== ''
        ? String(cfg.fecha_publicacion).trim().slice(0, 10)
        : null;
    return {
      edicion_id: parseInt(String(edicionId), 10),
      ciudad_id: parseInt(String(ciudad_id), 10),
      fecha_publicacion: fecha,
      habilitado: cfg?.habilitado === true,
    };
  });
}

// Crear nueva ediciÃ³n
export const addEdicion = async (edicionData: {
  nombre: string;
  nombre_link?: string | null;
  categoria?: string;
  trofeos?: string[];
  descripcion?: string;
  fecha_inicio?: string;
  fecha_fin?: string;
  fecha_anuncio?: string | null;
  fecha_catalogo?: string | null;
  logotema?: string;
  portada_web?: string;
  portada_mobile?: string;
  portada2_web?: string;
  portada2_mobile?: string;
  portada_app?: string;
  imagen_principal?: string;
  estado: EdicionEstado;
  items_votacion?: ItemVotacion[];
  ciudades_ids?: string[];
  /** Fecha + toggle de publicación de propuestas por ciudad. */
  ciudades_publicacion?: EdicionCiudadPublicacion[];
  patrocinadores_ids?: string[];
  restaurantes_ids?: string[];
  estado_automatico?: boolean;
  modo_prueba?: boolean;
  comprobante_reporte_ventas_obligatorio?: boolean;
  top3_clasica_peso_ventas?: number;
  top3_clasica_peso_pct_votos_hechos?: number;
  top3_clasica_peso_promedio?: number;
  top3_clasica_peso_pct_perfectos?: number;
  votacion_color_titulo?: string;
  votacion_color_subtitulo?: string;
  votacion_color_fondo?: string;
  votacion_color_boton?: string;
  votacion_color_texto_boton?: string;
  votacion_exitoso_color_titulo?: string;
  votacion_exitoso_color_subtitulo?: string;
  votacion_exitoso_color_fondo?: string;
  votacion_exitoso_color_boton?: string;
  votacion_exitoso_color_texto_boton?: string;
  votacion_exitoso_cta_texto?: string;
  votacion_exitoso_cta_texto_descriptivo?: string;
  votacion_exitoso_cta_link?: string;
  votacion_exitoso_cta_icono?: string;
  votacion_fallido_color_titulo?: string;
  votacion_fallido_color_subtitulo?: string;
  votacion_fallido_color_fondo?: string;
  votacion_fallido_color_boton?: string;
  votacion_fallido_color_texto_boton?: string;
  votacion_distancia_minima_metros?: number;
  catalogo_banner_colores?: CatalogoBannerColores;
}): Promise<Edicion> => {
  try {
    const {
      ciudades_ids,
      ciudades_publicacion,
      patrocinadores_ids,
      restaurantes_ids = [],
      catalogo_banner_colores,
      items_votacion,
      comprobante_reporte_ventas_obligatorio,
      top3_clasica_peso_ventas,
      top3_clasica_peso_pct_votos_hechos,
      top3_clasica_peso_promedio,
      top3_clasica_peso_pct_perfectos,
      votacion_color_titulo,
      votacion_color_subtitulo,
      votacion_color_fondo,
      votacion_color_boton,
      votacion_color_texto_boton,
      votacion_exitoso_color_titulo,
      votacion_exitoso_color_subtitulo,
      votacion_exitoso_color_fondo,
      votacion_exitoso_color_boton,
      votacion_exitoso_color_texto_boton,
      votacion_exitoso_cta_texto,
      votacion_exitoso_cta_texto_descriptivo,
      votacion_exitoso_cta_link,
      votacion_exitoso_cta_icono,
      votacion_fallido_color_titulo,
      votacion_fallido_color_subtitulo,
      votacion_fallido_color_fondo,
      votacion_fallido_color_boton,
      votacion_fallido_color_texto_boton,
      votacion_distancia_minima_metros,
      ...datosEdicion
    } = edicionData;
    
    // Preparar datos para Supabase, incluyendo items_votacion como JSONB
    // Filtrar items vacÃ­os antes de guardar
    const itemsVotacionFiltrados = items_votacion && items_votacion.length > 0
      ? items_votacion.filter(item => item.titulo && item.titulo.trim().length > 0)
      : null;
    
    // Filtrar campos que pueden no existir en la base de datos
    const nombreLinkNorm =
      datosEdicion.nombre_link != null && String(datosEdicion.nombre_link).trim() !== ''
        ? normalizeEdicionNombreLink(String(datosEdicion.nombre_link))
        : null;

    const datosParaInsertar: any = {
      nombre: datosEdicion.nombre,
      nombre_link: nombreLinkNorm,
      categoria: datosEdicion.categoria,
      trofeos: parseEdicionTrofeos(datosEdicion.trofeos),
      descripcion: datosEdicion.descripcion,
      fecha_inicio: datosEdicion.fecha_inicio,
      fecha_fin: datosEdicion.fecha_fin,
      fecha_anuncio:
        datosEdicion.fecha_anuncio && String(datosEdicion.fecha_anuncio).trim() !== ''
          ? String(datosEdicion.fecha_anuncio).trim()
          : null,
      fecha_catalogo:
        datosEdicion.fecha_catalogo && String(datosEdicion.fecha_catalogo).trim() !== ''
          ? String(datosEdicion.fecha_catalogo).trim()
          : null,
      estado: datosEdicion.estado,
      estado_automatico: datosEdicion.estado_automatico === true,
      modo_prueba: datosEdicion.modo_prueba === true,
      comprobante_reporte_ventas_obligatorio: comprobante_reporte_ventas_obligatorio !== false,
      precio_propuesta:
        typeof datosEdicion.precio_propuesta === 'number' &&
        Number.isFinite(datosEdicion.precio_propuesta) &&
        datosEdicion.precio_propuesta >= 0
          ? Math.round(datosEdicion.precio_propuesta * 100) / 100
          : null,
      items_votacion: itemsVotacionFiltrados && itemsVotacionFiltrados.length > 0 
        ? itemsVotacionFiltrados 
        : null,
      top3_clasica_peso_ventas:
        typeof top3_clasica_peso_ventas === 'number' && Number.isFinite(top3_clasica_peso_ventas)
          ? top3_clasica_peso_ventas
          : 20,
      top3_clasica_peso_pct_votos_hechos:
        typeof top3_clasica_peso_pct_votos_hechos === 'number' &&
        Number.isFinite(top3_clasica_peso_pct_votos_hechos)
          ? top3_clasica_peso_pct_votos_hechos
          : 20,
      top3_clasica_peso_promedio:
        typeof top3_clasica_peso_promedio === 'number' && Number.isFinite(top3_clasica_peso_promedio)
          ? top3_clasica_peso_promedio
          : 40,
      top3_clasica_peso_pct_perfectos:
        typeof top3_clasica_peso_pct_perfectos === 'number' &&
        Number.isFinite(top3_clasica_peso_pct_perfectos)
          ? top3_clasica_peso_pct_perfectos
          : 20,
      votacion_color_titulo:
        typeof votacion_color_titulo === 'string' && votacion_color_titulo.trim() !== ''
          ? votacion_color_titulo.trim()
          : '#000000',
      votacion_color_subtitulo:
        typeof votacion_color_subtitulo === 'string' && votacion_color_subtitulo.trim() !== ''
          ? votacion_color_subtitulo.trim()
          : '#A7ABB4',
      votacion_color_fondo:
        typeof votacion_color_fondo === 'string' && votacion_color_fondo.trim() !== ''
          ? votacion_color_fondo.trim()
          : '#FFFFFF',
      votacion_color_boton:
        typeof votacion_color_boton === 'string' && votacion_color_boton.trim() !== ''
          ? votacion_color_boton.trim()
          : '#A20000',
      votacion_color_texto_boton:
        typeof votacion_color_texto_boton === 'string' &&
        votacion_color_texto_boton.trim() !== ''
          ? votacion_color_texto_boton.trim()
          : '#FFFFFF',
      votacion_exitoso_color_titulo:
        typeof votacion_exitoso_color_titulo === 'string' &&
        votacion_exitoso_color_titulo.trim() !== ''
          ? votacion_exitoso_color_titulo.trim()
          : '#030213',
      votacion_exitoso_color_subtitulo:
        typeof votacion_exitoso_color_subtitulo === 'string' &&
        votacion_exitoso_color_subtitulo.trim() !== ''
          ? votacion_exitoso_color_subtitulo.trim()
          : '#030213',
      votacion_exitoso_color_fondo:
        typeof votacion_exitoso_color_fondo === 'string' &&
        votacion_exitoso_color_fondo.trim() !== ''
          ? votacion_exitoso_color_fondo.trim()
          : '#F9C22E',
      votacion_exitoso_color_boton:
        typeof votacion_exitoso_color_boton === 'string' &&
        votacion_exitoso_color_boton.trim() !== ''
          ? votacion_exitoso_color_boton.trim()
          : '#C50018',
      votacion_exitoso_color_texto_boton:
        typeof votacion_exitoso_color_texto_boton === 'string' &&
        votacion_exitoso_color_texto_boton.trim() !== ''
          ? votacion_exitoso_color_texto_boton.trim()
          : '#FFFFFF',
      votacion_exitoso_cta_texto:
        typeof votacion_exitoso_cta_texto === 'string' && votacion_exitoso_cta_texto.trim() !== ''
          ? votacion_exitoso_cta_texto.trim()
          : '¡Síguenos!',
      votacion_exitoso_cta_texto_descriptivo:
        typeof votacion_exitoso_cta_texto_descriptivo === 'string'
          ? votacion_exitoso_cta_texto_descriptivo
          : '',
      votacion_exitoso_cta_link:
        typeof votacion_exitoso_cta_link === 'string' && votacion_exitoso_cta_link.trim() !== ''
          ? votacion_exitoso_cta_link.trim()
          : 'https://www.instagram.com/asgastronomico/',
      votacion_exitoso_cta_icono:
        typeof votacion_exitoso_cta_icono === 'string' && votacion_exitoso_cta_icono.trim() !== ''
          ? votacion_exitoso_cta_icono.trim()
          : 'logo-instagram',
      votacion_fallido_color_titulo:
        typeof votacion_fallido_color_titulo === 'string' &&
        votacion_fallido_color_titulo.trim() !== ''
          ? votacion_fallido_color_titulo.trim()
          : '#030213',
      votacion_fallido_color_subtitulo:
        typeof votacion_fallido_color_subtitulo === 'string' &&
        votacion_fallido_color_subtitulo.trim() !== ''
          ? votacion_fallido_color_subtitulo.trim()
          : '#030213',
      votacion_fallido_color_fondo:
        typeof votacion_fallido_color_fondo === 'string' &&
        votacion_fallido_color_fondo.trim() !== ''
          ? votacion_fallido_color_fondo.trim()
          : '#F9C22E',
      votacion_fallido_color_boton:
        typeof votacion_fallido_color_boton === 'string' &&
        votacion_fallido_color_boton.trim() !== ''
          ? votacion_fallido_color_boton.trim()
          : '#A20000',
      votacion_fallido_color_texto_boton:
        typeof votacion_fallido_color_texto_boton === 'string' &&
        votacion_fallido_color_texto_boton.trim() !== ''
          ? votacion_fallido_color_texto_boton.trim()
          : '#FFFFFF',
      votacion_distancia_minima_metros:
        typeof votacion_distancia_minima_metros === 'number' &&
        Number.isFinite(votacion_distancia_minima_metros) &&
        votacion_distancia_minima_metros > 0
          ? Math.floor(votacion_distancia_minima_metros)
          : 500,
      catalogo_banner_colores: parseCatalogoBannerColores(catalogo_banner_colores),
    };

    // Solo agregar campos de imÃ¡genes si estÃ¡n definidos (evitar columnas que no existen)
    if (datosEdicion.portada_web !== undefined) {
      datosParaInsertar.portada_web = datosEdicion.portada_web;
    }
    if (datosEdicion.portada_mobile !== undefined) {
      datosParaInsertar.portada_mobile = datosEdicion.portada_mobile;
    }
    if (datosEdicion.imagen_principal !== undefined) {
      datosParaInsertar.imagen_principal = datosEdicion.imagen_principal;
    }
    
    // Estos campos solo se agregan si existen en la base de datos
    if (datosEdicion.portada2_web !== undefined) {
      datosParaInsertar.portada2_web = datosEdicion.portada2_web;
    }
    if (datosEdicion.portada2_mobile !== undefined) {
      datosParaInsertar.portada2_mobile = datosEdicion.portada2_mobile;
    }
    if (datosEdicion.portada_app !== undefined) {
      datosParaInsertar.portada_app = datosEdicion.portada_app;
    }
    // Campo logotema
    if (datosEdicion.logotema !== undefined) {
      datosParaInsertar.logotema = datosEdicion.logotema;
    }
    
    console.log('ðŸ“ Datos a insertar en ediciones:', JSON.stringify(datosParaInsertar, null, 2));

    // Insertar la ediciÃ³n
    const { data, error } = await supabase
      .from('ediciones')
      .insert([datosParaInsertar])
      .select()
      .single();

    if (error) {
      console.error('âŒ Error insertando ediciÃ³n en Supabase:', error);
      console.error('ðŸ“ Mensaje de error:', error.message);
      console.error('ðŸ“ Detalles del error:', error.details);
      console.error('ðŸ“ CÃ³digo del error:', error.code);
      console.error('ðŸ“ Datos que se intentaron insertar:', JSON.stringify(datosParaInsertar, null, 2));
      throw error;
    }
    
    console.log('âœ… EdiciÃ³n insertada correctamente:', data);

    // Insertar relaciones con ciudades
    if (ciudades_ids && ciudades_ids.length > 0) {
      const ciudadesRelaciones = buildEdicionesCiudadesRows(
        data.id,
        ciudades_ids,
        ciudades_publicacion
      );
      
      const { error: errorCiudades } = await supabase
        .from('ediciones_ciudades')
        .insert(ciudadesRelaciones);
      
      if (errorCiudades) {
        console.error('Error insertando relaciones con ciudades:', errorCiudades);
      }
    }

    // Insertar relaciones con patrocinadores
    if (patrocinadores_ids && patrocinadores_ids.length > 0) {
      const patrocinadoresRelaciones = patrocinadores_ids.map(patrocinador_id => ({
        edicion_id: data.id,
        patrocinador_id: parseInt(patrocinador_id)
      }));
      
      const { error: errorPatrocinadores } = await supabase
        .from('ediciones_patrocinadores')
        .insert(patrocinadoresRelaciones);
      
      if (errorPatrocinadores) {
        console.error('Error insertando relaciones con patrocinadores:', errorPatrocinadores);
      }
    }

    // Insertar relaciones con restaurantes
    if (restaurantes_ids && restaurantes_ids.length > 0) {
      const restaurantesRelaciones = restaurantes_ids.map(restaurante_id => ({
        edicion_id: data.id,
        restaurante_id: parseInt(restaurante_id)
      }));
      
      const { error: errorRestaurantes } = await supabase
        .from('restaurantes_ediciones')
        .insert(restaurantesRelaciones);
      
      if (errorRestaurantes) {
        console.error('Error insertando relaciones con restaurantes:', errorRestaurantes);
      }
    }

    // Obtener la ediciÃ³n completa con todas las relaciones
    const edicionCompleta = await getEdicionById(String(data.id));
    if (!edicionCompleta) {
      throw new Error('No se pudo obtener la ediciÃ³n creada');
    }
    return edicionCompleta;
  } catch (error) {
    console.error('Error agregando ediciÃ³n:', error);
    throw error;
  }
};

// Actualizar ediciÃ³n
export const updateEdicion = async (id: string, edicionData: {
  nombre: string;
  nombre_link?: string | null;
  categoria?: string;
  trofeos?: string[];
  descripcion?: string;
  fecha_inicio?: string;
  fecha_fin?: string;
  fecha_anuncio?: string | null;
  fecha_catalogo?: string | null;
  logotema?: string;
  portada_web?: string;
  portada_mobile?: string;
  portada2_web?: string;
  portada2_mobile?: string;
  portada_app?: string;
  imagen_principal?: string;
  estado: EdicionEstado;
  items_votacion?: ItemVotacion[];
  ciudades_ids?: string[];
  /** Fecha + toggle de publicación de propuestas por ciudad. */
  ciudades_publicacion?: EdicionCiudadPublicacion[];
  patrocinadores_ids?: string[];
  restaurantes_ids?: string[];
  estado_automatico?: boolean;
  modo_prueba?: boolean;
  comprobante_reporte_ventas_obligatorio?: boolean;
  top3_clasica_peso_ventas?: number;
  top3_clasica_peso_pct_votos_hechos?: number;
  top3_clasica_peso_promedio?: number;
  top3_clasica_peso_pct_perfectos?: number;
  votacion_color_titulo?: string;
  votacion_color_subtitulo?: string;
  votacion_color_fondo?: string;
  votacion_color_boton?: string;
  votacion_color_texto_boton?: string;
  votacion_exitoso_color_titulo?: string;
  votacion_exitoso_color_subtitulo?: string;
  votacion_exitoso_color_fondo?: string;
  votacion_exitoso_color_boton?: string;
  votacion_exitoso_color_texto_boton?: string;
  votacion_exitoso_cta_texto?: string;
  votacion_exitoso_cta_texto_descriptivo?: string;
  votacion_exitoso_cta_link?: string;
  votacion_exitoso_cta_icono?: string;
  votacion_fallido_color_titulo?: string;
  votacion_fallido_color_subtitulo?: string;
  votacion_fallido_color_fondo?: string;
  votacion_fallido_color_boton?: string;
  votacion_fallido_color_texto_boton?: string;
  votacion_distancia_minima_metros?: number;
  catalogo_banner_colores?: CatalogoBannerColores;
}): Promise<Edicion> => {
  try {
    const {
      ciudades_ids,
      ciudades_publicacion,
      patrocinadores_ids,
      restaurantes_ids,
      catalogo_banner_colores,
      items_votacion,
      comprobante_reporte_ventas_obligatorio,
      top3_clasica_peso_ventas,
      top3_clasica_peso_pct_votos_hechos,
      top3_clasica_peso_promedio,
      top3_clasica_peso_pct_perfectos,
      votacion_color_titulo,
      votacion_color_subtitulo,
      votacion_color_fondo,
      votacion_color_boton,
      votacion_color_texto_boton,
      votacion_exitoso_color_titulo,
      votacion_exitoso_color_subtitulo,
      votacion_exitoso_color_fondo,
      votacion_exitoso_color_boton,
      votacion_exitoso_color_texto_boton,
      votacion_exitoso_cta_texto,
      votacion_exitoso_cta_texto_descriptivo,
      votacion_exitoso_cta_link,
      votacion_exitoso_cta_icono,
      votacion_fallido_color_titulo,
      votacion_fallido_color_subtitulo,
      votacion_fallido_color_fondo,
      votacion_fallido_color_boton,
      votacion_fallido_color_texto_boton,
      votacion_distancia_minima_metros,
      ...datosEdicion
    } = edicionData;
    
    console.log('🔍 Actualizando edición con restaurantes_ids:', restaurantes_ids);
    
    // Preparar datos para Supabase, incluyendo items_votacion como JSONB
    // Filtrar items vacÃ­os antes de guardar
    const itemsVotacionFiltrados = items_votacion && items_votacion.length > 0
      ? items_votacion.filter(item => item.titulo && item.titulo.trim().length > 0)
      : null;
    
    // Filtrar campos que pueden no existir en la base de datos
    const nombreLinkNorm =
      datosEdicion.nombre_link !== undefined
        ? datosEdicion.nombre_link == null || String(datosEdicion.nombre_link).trim() === ''
          ? null
          : normalizeEdicionNombreLink(String(datosEdicion.nombre_link))
        : undefined;

    const datosParaActualizar: any = {
      nombre: datosEdicion.nombre,
      categoria: datosEdicion.categoria,
      descripcion: datosEdicion.descripcion,
      fecha_inicio: datosEdicion.fecha_inicio,
      fecha_fin: datosEdicion.fecha_fin,
      estado: datosEdicion.estado,
      items_votacion: itemsVotacionFiltrados && itemsVotacionFiltrados.length > 0 
        ? itemsVotacionFiltrados 
        : null,
    };
    if (datosEdicion.trofeos !== undefined) {
      datosParaActualizar.trofeos = parseEdicionTrofeos(datosEdicion.trofeos);
    }
    if (nombreLinkNorm !== undefined) {
      datosParaActualizar.nombre_link = nombreLinkNorm;
    }
    if (datosEdicion.estado_automatico !== undefined) {
      datosParaActualizar.estado_automatico = datosEdicion.estado_automatico === true;
    }
    if (datosEdicion.modo_prueba !== undefined) {
      datosParaActualizar.modo_prueba = datosEdicion.modo_prueba === true;
    }
    if (comprobante_reporte_ventas_obligatorio !== undefined) {
      datosParaActualizar.comprobante_reporte_ventas_obligatorio =
        comprobante_reporte_ventas_obligatorio !== false;
    }
    if (datosEdicion.precio_propuesta !== undefined) {
      datosParaActualizar.precio_propuesta =
        typeof datosEdicion.precio_propuesta === 'number' &&
        Number.isFinite(datosEdicion.precio_propuesta) &&
        datosEdicion.precio_propuesta >= 0
          ? Math.round(datosEdicion.precio_propuesta * 100) / 100
          : null;
    }
    if (top3_clasica_peso_ventas !== undefined) {
      datosParaActualizar.top3_clasica_peso_ventas = top3_clasica_peso_ventas;
    }
    if (top3_clasica_peso_pct_votos_hechos !== undefined) {
      datosParaActualizar.top3_clasica_peso_pct_votos_hechos = top3_clasica_peso_pct_votos_hechos;
    }
    if (top3_clasica_peso_promedio !== undefined) {
      datosParaActualizar.top3_clasica_peso_promedio = top3_clasica_peso_promedio;
    }
    if (top3_clasica_peso_pct_perfectos !== undefined) {
      datosParaActualizar.top3_clasica_peso_pct_perfectos = top3_clasica_peso_pct_perfectos;
    }
    if (votacion_color_titulo !== undefined) {
      datosParaActualizar.votacion_color_titulo = votacion_color_titulo;
    }
    if (votacion_color_subtitulo !== undefined) {
      datosParaActualizar.votacion_color_subtitulo = votacion_color_subtitulo;
    }
    if (votacion_color_fondo !== undefined) {
      datosParaActualizar.votacion_color_fondo = votacion_color_fondo;
    }
    if (votacion_color_boton !== undefined) {
      datosParaActualizar.votacion_color_boton = votacion_color_boton;
    }
    if (votacion_color_texto_boton !== undefined) {
      datosParaActualizar.votacion_color_texto_boton = votacion_color_texto_boton;
    }
    if (votacion_exitoso_color_titulo !== undefined) {
      datosParaActualizar.votacion_exitoso_color_titulo = votacion_exitoso_color_titulo;
    }
    if (votacion_exitoso_color_subtitulo !== undefined) {
      datosParaActualizar.votacion_exitoso_color_subtitulo = votacion_exitoso_color_subtitulo;
    }
    if (votacion_exitoso_color_fondo !== undefined) {
      datosParaActualizar.votacion_exitoso_color_fondo = votacion_exitoso_color_fondo;
    }
    if (votacion_exitoso_color_boton !== undefined) {
      datosParaActualizar.votacion_exitoso_color_boton = votacion_exitoso_color_boton;
    }
    if (votacion_exitoso_color_texto_boton !== undefined) {
      datosParaActualizar.votacion_exitoso_color_texto_boton = votacion_exitoso_color_texto_boton;
    }
    if (votacion_exitoso_cta_texto !== undefined) {
      datosParaActualizar.votacion_exitoso_cta_texto = votacion_exitoso_cta_texto;
    }
    if (votacion_exitoso_cta_texto_descriptivo !== undefined) {
      datosParaActualizar.votacion_exitoso_cta_texto_descriptivo =
        votacion_exitoso_cta_texto_descriptivo;
    }
    if (votacion_exitoso_cta_link !== undefined) {
      datosParaActualizar.votacion_exitoso_cta_link = votacion_exitoso_cta_link;
    }
    if (votacion_exitoso_cta_icono !== undefined) {
      datosParaActualizar.votacion_exitoso_cta_icono = votacion_exitoso_cta_icono;
    }
    if (votacion_fallido_color_titulo !== undefined) {
      datosParaActualizar.votacion_fallido_color_titulo = votacion_fallido_color_titulo;
    }
    if (votacion_fallido_color_subtitulo !== undefined) {
      datosParaActualizar.votacion_fallido_color_subtitulo = votacion_fallido_color_subtitulo;
    }
    if (votacion_fallido_color_fondo !== undefined) {
      datosParaActualizar.votacion_fallido_color_fondo = votacion_fallido_color_fondo;
    }
    if (votacion_fallido_color_boton !== undefined) {
      datosParaActualizar.votacion_fallido_color_boton = votacion_fallido_color_boton;
    }
    if (votacion_fallido_color_texto_boton !== undefined) {
      datosParaActualizar.votacion_fallido_color_texto_boton = votacion_fallido_color_texto_boton;
    }
    if (votacion_distancia_minima_metros !== undefined) {
      datosParaActualizar.votacion_distancia_minima_metros = votacion_distancia_minima_metros;
    }
    if (catalogo_banner_colores !== undefined) {
      datosParaActualizar.catalogo_banner_colores =
        parseCatalogoBannerColores(catalogo_banner_colores);
    }
    if (datosEdicion.fecha_anuncio !== undefined) {
      datosParaActualizar.fecha_anuncio =
        datosEdicion.fecha_anuncio && String(datosEdicion.fecha_anuncio).trim() !== ''
          ? String(datosEdicion.fecha_anuncio).trim()
          : null;
    }
    if (datosEdicion.fecha_catalogo !== undefined) {
      datosParaActualizar.fecha_catalogo =
        datosEdicion.fecha_catalogo && String(datosEdicion.fecha_catalogo).trim() !== ''
          ? String(datosEdicion.fecha_catalogo).trim()
          : null;
    }
    
    // Preservar campos que no se editan en el formulario pero existen en la base de datos
    if ((datosEdicion as any).formato_tarjeta !== undefined) {
      datosParaActualizar.formato_tarjeta = (datosEdicion as any).formato_tarjeta;
    }
    if ((datosEdicion as any).horario !== undefined) {
      datosParaActualizar.horario = (datosEdicion as any).horario;
    }

    // Solo agregar campos de imÃ¡genes si estÃ¡n definidos (evitar columnas que no existen)
    if (datosEdicion.portada_web !== undefined) {
      datosParaActualizar.portada_web = datosEdicion.portada_web;
    }
    if (datosEdicion.portada_mobile !== undefined) {
      datosParaActualizar.portada_mobile = datosEdicion.portada_mobile;
    }
    if (datosEdicion.imagen_principal !== undefined) {
      datosParaActualizar.imagen_principal = datosEdicion.imagen_principal;
    }
    
    // Estos campos solo se agregan si existen en la base de datos
    if (datosEdicion.portada2_web !== undefined) {
      datosParaActualizar.portada2_web = datosEdicion.portada2_web;
    }
    if (datosEdicion.portada2_mobile !== undefined) {
      datosParaActualizar.portada2_mobile = datosEdicion.portada2_mobile;
    }
    if (datosEdicion.portada_app !== undefined) {
      datosParaActualizar.portada_app = datosEdicion.portada_app;
    }
    // Campo logotema
    if (datosEdicion.logotema !== undefined) {
      datosParaActualizar.logotema = datosEdicion.logotema;
    }
    
    console.log('ðŸ“ Datos a actualizar en ediciones:', JSON.stringify(datosParaActualizar, null, 2));

    // Actualizar la ediciÃ³n
    const { data, error } = await supabase
      .from('ediciones')
      .update(datosParaActualizar)
      .eq('id', id)
      .select()
      .single();

    if (error) {
      console.error('âŒ Error actualizando ediciÃ³n en Supabase:', error);
      console.error('ðŸ“ Mensaje de error:', error.message);
      console.error('ðŸ“ Detalles del error:', error.details);
      console.error('ðŸ“ CÃ³digo del error:', error.code);
      console.error('ðŸ“ Datos que se intentaron actualizar:', JSON.stringify(datosParaActualizar, null, 2));
      throw error;
    }
    
    console.log('âœ… EdiciÃ³n actualizada correctamente:', data);

    // Actualizar relaciones con ciudades
    if (ciudades_ids !== undefined) {
      // Eliminar relaciones existentes
      await supabase
        .from('ediciones_ciudades')
        .delete()
        .eq('edicion_id', id);
      
      // Insertar nuevas relaciones
      if (ciudades_ids.length > 0) {
        const ciudadesRelaciones = buildEdicionesCiudadesRows(
          id,
          ciudades_ids,
          ciudades_publicacion
        );
        
        const { error: errorCiudades } = await supabase
          .from('ediciones_ciudades')
          .insert(ciudadesRelaciones);
        
        if (errorCiudades) {
          console.error('Error actualizando relaciones con ciudades:', errorCiudades);
        }
      }
    }

    // Actualizar relaciones con patrocinadores
    if (patrocinadores_ids !== undefined) {
      // Eliminar relaciones existentes
      await supabase
        .from('ediciones_patrocinadores')
        .delete()
        .eq('edicion_id', id);
      
      // Insertar nuevas relaciones
      if (patrocinadores_ids.length > 0) {
        const patrocinadoresRelaciones = patrocinadores_ids.map(patrocinador_id => ({
          edicion_id: parseInt(id),
          patrocinador_id: parseInt(patrocinador_id)
        }));
        
        const { error: errorPatrocinadores } = await supabase
          .from('ediciones_patrocinadores')
          .insert(patrocinadoresRelaciones);
        
        if (errorPatrocinadores) {
          console.error('Error actualizando relaciones con patrocinadores:', errorPatrocinadores);
        }
      }
    }

    // Actualizar relaciones con restaurantes
    if (restaurantes_ids !== undefined) {
      console.log('🔍 Procesando restaurantes_ids:', restaurantes_ids);

      // Seguridad: un payload vacío suele ocurrir al editar otros campos (p. ej. precio)
      // sin tener cargada la lista de participantes; no borrar vínculos existentes.
      if (!Array.isArray(restaurantes_ids) || restaurantes_ids.length === 0) {
        console.warn(
          '⚠️ restaurantes_ids vacío: se conservan los vínculos existentes en restaurantes_ediciones'
        );
      } else {
        // Eliminar relaciones existentes
        const { error: deleteError } = await supabase
          .from('restaurantes_ediciones')
          .delete()
          .eq('edicion_id', id);

        if (deleteError) {
          console.error('❌ Error eliminando relaciones existentes con restaurantes:', deleteError);
        } else {
          console.log('✅ Relaciones existentes eliminadas correctamente');
        }

        const restaurantesRelaciones = restaurantes_ids.map((restaurante_id) => ({
          edicion_id: parseInt(id, 10),
          restaurante_id: parseInt(String(restaurante_id), 10),
        }));

        console.log('🔍 Insertando nuevas relaciones:', restaurantesRelaciones);
        const { data: insertData, error: errorRestaurantes } = await supabase
          .from('restaurantes_ediciones')
          .insert(restaurantesRelaciones)
          .select();

        if (errorRestaurantes) {
          console.error('❌ Error insertando relaciones con restaurantes:', errorRestaurantes);
        } else {
          console.log('✅ Relaciones con restaurantes insertadas correctamente:', insertData);
        }
      }
    } else {
      console.log('ℹ️ restaurantes_ids es undefined, no se actualizan las relaciones');
    }

    // Obtener la ediciÃ³n actualizada con todas las relaciones
    const edicionActualizada = await getEdicionById(id);
    if (!edicionActualizada) {
      throw new Error('No se pudo obtener la ediciÃ³n actualizada');
    }
    return edicionActualizada;
  } catch (error) {
    console.error('Error actualizando ediciÃ³n:', error);
    throw error;
  }
};

/** Solo actualiza `items_votacion` (p. ej. qué ítems cuentan en Top 3). */
export const patchEdicionItemsVotacion = async (
  id: string | number,
  items: ItemVotacion[]
): Promise<ItemVotacion[]> => {
  const filtrados = (items ?? [])
    .map((item) => serializeItemVotacion(item))
    .filter((item) => item.titulo.length > 0);
  const { error } = await supabase
    .from('ediciones')
    .update({ items_votacion: filtrados.length > 0 ? filtrados : [] })
    .eq('id', id);
  if (error) throw error;
  return filtrados;
};

// Eliminar ediciÃ³n
export const deleteEdicion = async (id: string): Promise<void> => {
  try {
    const { error } = await supabase
      .from('ediciones')
      .delete()
      .eq('id', id);

    if (error) throw error;
  } catch (error) {
    console.error('Error eliminando ediciÃ³n:', error);
    throw error;
  }
};

// Buscar ediciones
export const searchEdiciones = async (query: string): Promise<Edicion[]> => {
  try {
    const { data, error } = await supabase
      .from('ediciones')
      .select('*')
      .or(`nombre.ilike.%${query}%,descripcion.ilike.%${query}%,categoria.ilike.%${query}%`)
      .order('fecha_creacion', { ascending: false });

    if (error) throw error;

    // Obtener todas las ediciones encontradas con sus relaciones
    const ediciones = await Promise.all(
      data.map(async (edicion: any) => {
        return await getEdicionById(String(edicion.id));
      })
    );

    return ediciones.filter((edicion): edicion is Edicion => edicion !== null);
  } catch (error) {
    console.error('Error buscando ediciones:', error);
    return [];
  }
};

// ========================================
// FUNCIONES PARA VOTOS
// ========================================

// Obtener votos por restaurante y ediciÃ³n
export const getVotosByRestauranteAndEdicion = async (restauranteId: string, edicionId: string): Promise<Voto[]> => {
  try {
    const { data, error } = await supabase
      .from('votos')
      .select(`
        *,
        restaurantes(id, nombre_mostrar),
        ediciones(id, nombre)
      `)
      .eq('restaurante_id', restauranteId)
      .eq('edicion_id', edicionId)
      .order('fecha_creacion', { ascending: false });

    if (error) throw error;

    return data.map((voto: any) => ({
      id: voto.id.toString(),
      restaurante_id: voto.restaurante_id.toString(),
      restaurante_nombre: voto.restaurantes?.nombre_mostrar,
      edicion_id: voto.edicion_id.toString(),
      edicion_nombre: voto.ediciones?.nombre,
      usuario_id: voto.usuario_id,
      usuario_nombre: voto.usuario_nombre,
      puntuacion: voto.puntuacion,
      comentario: voto.comentario,
      fecha_creacion: voto.fecha_creacion,
    }));
  } catch (error) {
    console.error('Error obteniendo votos:', error);
    return [];
  }
};

// Obtener votos por usuario
export const getVotosByUsuario = async (usuarioId: string): Promise<Voto[]> => {
  try {
    const { data, error } = await supabase
      .from('votos')
      .select(`
        *,
        restaurantes(id, nombre_mostrar),
        ediciones(id, nombre)
      `)
      .eq('usuario_id', usuarioId)
      .order('fecha_creacion', { ascending: false });

    if (error) throw error;

    return data.map((voto: any) => ({
      id: voto.id.toString(),
      restaurante_id: voto.restaurante_id.toString(),
      restaurante_nombre: voto.restaurantes?.nombre_mostrar,
      edicion_id: voto.edicion_id.toString(),
      edicion_nombre: voto.ediciones?.nombre,
      usuario_id: voto.usuario_id,
      usuario_nombre: voto.usuario_nombre,
      puntuacion: voto.puntuacion,
      comentario: voto.comentario,
      fecha_creacion: voto.fecha_creacion,
    }));
  } catch (error) {
    console.error('Error obteniendo votos del usuario:', error);
    return [];
  }
};

// Crear nuevo voto
export const addVoto = async (votoData: {
  restaurante_id: string;
  edicion_id: string;
  usuario_id?: string;
  usuario_nombre?: string;
  puntuacion: number;
  comentario?: string;
}): Promise<Voto> => {
  try {
    const datosParaInsertar = {
      ...votoData,
      restaurante_id: parseInt(votoData.restaurante_id),
      edicion_id: parseInt(votoData.edicion_id),
    };

    const { data, error } = await supabase
      .from('votos')
      .insert([datosParaInsertar])
      .select(`
        *,
        restaurantes(id, nombre_mostrar),
        ediciones(id, nombre)
      `)
      .single();

    if (error) throw error;

    return {
      id: data.id.toString(),
      restaurante_id: data.restaurante_id.toString(),
      restaurante_nombre: data.restaurantes?.nombre_mostrar,
      edicion_id: data.edicion_id.toString(),
      edicion_nombre: data.ediciones?.nombre,
      usuario_id: data.usuario_id,
      usuario_nombre: data.usuario_nombre,
      puntuacion: data.puntuacion,
      comentario: data.comentario,
      fecha_creacion: data.fecha_creacion,
    };
  } catch (error) {
    console.error('Error agregando voto:', error);
    throw error;
  }
};

// Actualizar voto
export const updateVoto = async (id: string, votoData: {
  puntuacion: number;
  comentario?: string;
}): Promise<Voto> => {
  try {
    const { data, error } = await supabase
      .from('votos')
      .update(votoData)
      .eq('id', id)
      .select(`
        *,
        restaurantes(id, nombre_mostrar),
        ediciones(id, nombre)
      `)
      .single();

    if (error) throw error;

    return {
      id: data.id.toString(),
      restaurante_id: data.restaurante_id.toString(),
      restaurante_nombre: data.restaurantes?.nombre_mostrar,
      edicion_id: data.edicion_id.toString(),
      edicion_nombre: data.ediciones?.nombre,
      usuario_id: data.usuario_id,
      usuario_nombre: data.usuario_nombre,
      puntuacion: data.puntuacion,
      comentario: data.comentario,
      fecha_creacion: data.fecha_creacion,
    };
  } catch (error) {
    console.error('Error actualizando voto:', error);
    throw error;
  }
};

// Eliminar voto
export const deleteVoto = async (id: string): Promise<void> => {
  try {
    const { error } = await supabase
      .from('votos')
      .delete()
      .eq('id', id);

    if (error) throw error;
  } catch (error) {
    console.error('Error eliminando voto:', error);
    throw error;
  }
};

// Verificar si un usuario ya votÃ³
export const hasVoted = async (usuarioId: string, restauranteId: string, edicionId: string): Promise<boolean> => {
  try {
    const { data, error } = await supabase
      .from('votos')
      .select('id')
      .eq('usuario_id', usuarioId)
      .eq('restaurante_id', restauranteId)
      .eq('edicion_id', edicionId)
      .single();

    if (error && error.code !== 'PGRST116') throw error; // PGRST116 = no rows returned
    return !!data;
  } catch (error) {
    console.error('Error verificando voto:', error);
    return false;
  }
};

// ========================================
// FUNCIONES PARA ESTADÃSTICAS
// ========================================

// Obtener estadÃ­sticas por restaurante
export const getEstadisticas = async (restauranteId?: string): Promise<Estadistica[]> => {
  try {
    let query = supabase
      .from('estadisticas')
      .select(`
        *,
        restaurantes(id, nombre_mostrar),
        ediciones(id, nombre)
      `)
      .order('fecha_actualizacion', { ascending: false });

    if (restauranteId) {
      query = query.eq('restaurante_id', restauranteId);
    }

    const { data, error } = await query;

    if (error) throw error;

    return data.map((estadistica: any) => ({
      id: estadistica.id.toString(),
      restaurante_id: estadistica.restaurante_id.toString(),
      restaurante_nombre: estadistica.restaurantes?.nombre_mostrar,
      edicion_id: estadistica.edicion_id.toString(),
      edicion_nombre: estadistica.ediciones?.nombre,
      total_votos: estadistica.total_votos,
      promedio_puntuacion: estadistica.promedio_puntuacion,
      fecha_actualizacion: estadistica.fecha_actualizacion,
    }));
  } catch (error) {
    console.error('Error obteniendo estadÃ­sticas:', error);
    return [];
  }
};

// Obtener estadÃ­sticas por ediciÃ³n
export const getEstadisticasByEdicion = async (edicionId: string): Promise<Estadistica[]> => {
  try {
    const { data, error } = await supabase
      .from('estadisticas')
      .select(`
        *,
        restaurantes(id, nombre_mostrar),
        ediciones(id, nombre)
      `)
      .eq('edicion_id', edicionId)
      .order('promedio_puntuacion', { ascending: false });

    if (error) throw error;

    return data.map((estadistica: any) => ({
      id: estadistica.id.toString(),
      restaurante_id: estadistica.restaurante_id.toString(),
      restaurante_nombre: estadistica.restaurantes?.nombre_mostrar,
      edicion_id: estadistica.edicion_id.toString(),
      edicion_nombre: estadistica.ediciones?.nombre,
      total_votos: estadistica.total_votos,
      promedio_puntuacion: estadistica.promedio_puntuacion,
      fecha_actualizacion: estadistica.fecha_actualizacion,
    }));
  } catch (error) {
    console.error('Error obteniendo estadÃ­sticas por ediciÃ³n:', error);
    return [];
  }
};

// ========================================
// FUNCIONES PARA PROPUESTAS
// ========================================

// Obtener todas las propuestas
// Si ciudades_ids está presente, filtra propuestas por restaurantes de esas ciudades (para admins)
export const getPropuestas = async (ciudades_ids?: string[]): Promise<Propuesta[]> => {
  try {
    let query = supabase
      .from('propuestas')
      .select(`
        *,
        restaurantes!inner(
          id,
          ciudad_id,
          ciudades(id, nombre)
        )
      `);

    // Si se proporcionan ciudades_ids, filtrar por restaurantes de esas ciudades (para admins)
    if (ciudades_ids && ciudades_ids.length > 0) {
      const ciudadIdsInt = ciudades_ids.map(id => parseInt(id)).filter(id => !isNaN(id));
      if (ciudadIdsInt.length > 0) {
        query = query.in('restaurantes.ciudad_id', ciudadIdsInt);
      }
    }

    const { data, error } = await query;

    if (error) throw error;

    return data.map((propuesta: any) => ({
      id: propuesta.id.toString(),
      nombre: propuesta.nombre || propuesta.titulo || '',
      descripcion: propuesta.descripcion,
      fecha_creacion: propuesta.fecha_creacion || propuesta.created_at || new Date().toISOString(),
    }));
  } catch (error) {
    console.error('Error obteniendo propuestas:', error);
    return [];
  }
};
